/**
 * Message
 *
 * Renders a chat message with assistant output, annotations, and actions.
 *
 * Primary responsibilities:
 * - Display user and assistant messages
 * - Render generated artifacts, annotations, and action controls
 * - Handle copy/share interactions for meaningful generated output
 *
 * Dependencies:
 * - Message markdown renderer
 * - Analytics and Space item action helpers
 */

'use client'

import React from 'react'
import dynamic from 'next/dynamic'

import { FC, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { updateMessage, updateMessageFeedback } from '@/db/messages'
import { chatUpdateManager } from '@/lib/events/chatUpdates'
import { getMessageImageFromStorage } from '@/db/storage/message-images'
import {
  getSafeRenderableImageUrl,
  normalizeMessageImageStorageSource
} from '@/lib/media/safe-url'
import logo from '@/public/logo.png'
import { Json, Tables, TablesUpdate } from '@/supabase/types'
import { CodeBlock, LLM, LLMID, MessageImage, ModelProvider } from '@/types'
import {
  IconApi,
  IconArchive,
  IconBulb,
  IconCaretDownFilled,
  IconCaretRightFilled,
  IconCircleFilled,
  IconFileText,
  IconMoodSmile,
  IconPuzzle,
  IconWorld,
  IconCaretUpFilled
} from '@tabler/icons-react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import katex from 'katex'
import MarkdownIt from 'markdown-it'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import {
  Annotation,
  Annotation2,
  MessageRenderSegment,
  ToolTimelineEntry
} from '@/types/annotation'
import { ChatMessage, CodeBlock as ChatMessageCodeBlock } from '@/types/chat-message'
import type { RegenerateRequest } from '@/lib/chat/regenerate-options'
import {
  reconstructContentWithCodeBlocks,
  reconstructContentWithCodeBlocksInChatMessage
} from '@/lib/messages'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { cn } from '@/lib/utils'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import { AssistantIcon } from '@/components/assistants/assistant-icon'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { SelectedHtmlElements } from '@/components/messages/annotations/selectedHtmlElements'
import { ResponseTime, ToolCalls } from '@/components/messages/annotations/toolCalls'
import { WebSearch } from '@/components/messages/annotations/websearch'
import { YouTube } from '@/components/messages/annotations/youtube'
import { AnnotationWeather } from '@/components/messages/annotations/weather'
import { LoadingMessage } from '@/components/messages/message-loading'
import { Task } from './annotations/task'
import type { YahooFinanceCard as YahooFinanceCardComponent } from './annotations/yahoo-finance-card'

// chart.js (~60 KB gz) loads only when a stock card is rendered
const LazyYahooFinanceCard = dynamic(
  () => import('./annotations/yahoo-finance-card').then((module) => module.YahooFinanceCard),
  { loading: () => null }
)
const YahooFinanceCard: React.FC<React.ComponentProps<typeof YahooFinanceCardComponent>> = (
  props
) => <LazyYahooFinanceCard {...props} />
import { TimeAnnotation } from './annotations/time'
import DeepResearchAnnotation from '@/components/messages/annotations/deepResearch'
import LosiCodeAnnotation from '@/components/messages/annotations/losi-code'
import AudioToolAnnotation from '@/components/messages/annotations/audio'
import AnnotationPresentation from '@/components/messages/annotations/presentation'
import ThreeDAssetAnnotation from '@/components/messages/annotations/three-d-asset'
import DesktopToolAnnotation, {
  getDesktopToolAnnotationResponseTimeLabel,
  isDesktopToolAnnotationKey
} from '@/components/messages/annotations/desktop-tool'
import {
  CrmCreateLeadAnnotation,
  CrmUpdateLeadAnnotation,
  CrmSearchLeadsAnnotation,
  CrmGetLeadDetailsAnnotation,
  CrmGetEmailEngagementAnnotation,
  CrmGetLeadInsightsAnnotation,
  CrmSuggestFollowUpAnnotation,
  CrmSendEmailAnnotation,
  CrmSendSmsAnnotation,
  CrmScheduleActivityAnnotation,
  NexusListAgentsAnnotation,
  NexusGetAgentAnnotation,
  NexusCreateAgentAnnotation,
  NexusUpdateAgentAnnotation,
  NexusImproveAgentAnnotation,
  NexusCrmUnifiedAnnotation,
  GenericNexusCrmAnnotation
} from '@/components/messages/annotations/nexus-tools'
import {
  ToolActivityLog,
  isToolActivityKey
} from '@/components/messages/annotations/tool-activity-log'
import {
  GeneratedIntegrationDraftAnnotation,
  renderToolResultForEntry
} from '@/components/messages/annotations/tool-results'
import { ToolTimeline } from '@/components/messages/annotations/tool-timeline'
import { WorkedForGroup } from '@/components/messages/worked-for-group'
import { buildWorkedForBlocks } from '@/lib/chat/worked-for-blocks'
import { VirtualFileCreatedAnnotation } from '@/components/messages/annotations/virtual-file-created'
import { PlanCreatedAnnotation } from '@/components/messages/annotations/plan-created-annotation'
import { SidecarRunAnnotation } from '@/components/messages/annotations/sidecar-run'
import { SignpostingPlacesAnnotation } from '@/components/messages/annotations/signposting-places'
import { SignpostingLocationRequestAnnotation } from '@/components/messages/annotations/signposting-location-request'
import { CredentialVaultLoginRequestAnnotation } from '@/components/messages/annotations/credential-vault-login-request'
import { ConnectorRequestAnnotation } from '@/components/messages/annotations/connector-request'
import { LiveScoreAnnotation } from '@/components/messages/annotations/live-score'
import { EventDiscoveryAnnotation } from '@/components/messages/annotations/event-discovery'
import { SmartReminderAnnotation } from '@/components/messages/annotations/smart-reminder'
import { PriceComparisonAnnotation } from '@/components/messages/annotations/price-comparison'
import { ContentEngineApprovalCard } from '@/components/messages/content-engine-approval-card'
import { PlanApprovalCard } from '@/components/messages/plan-approval-card'
import type { ChatTodoItem } from '@/db/chat-todos'

import { ModelIcon } from '../models/model-icon'
import { Button } from '../ui/button'
import { FileIcon } from '../ui/file-icon'
import { FilePreview } from '../ui/file-preview'
import { GradualSpacing } from '../ui/gradual-spacing'
import { TextareaAutosize } from '../ui/textarea-autosize'
import { WithTooltip } from '../ui/with-tooltip'
import { MessageActions } from './message-actions'
import { MessageLosiMark } from '@/components/losi-bot/message-losi-mark'
import {
  resolveChatLosiPose,
  type ChatLosiPose
} from '@/lib/losi-avatar/chat-status'
import {
  getCrewChatRosterForChat,
  getCrewMemberForChat
} from '@/lib/crew/crew-chat-binding'
import {
  parseCrewChatActiveSpeakerMateId,
  type CrewChatRoster
} from '@/lib/crew/crew-chat'
import { mateIdToFeedShape, MATE_BY_ID } from '@/lib/crew/mates'
import {
  getMateRenderIdentity,
  type CrewMateRenderIdentity
} from '@/lib/crew/mate-identity'
import {
  type CreatedSpaceTask,
  type CreatedSpaceNote,
  type SpaceNoteDestination
} from './add-to-space-action'
import { SpaceCreatedItemBlock } from './space-created-item-block'
import { MessageMarkdown } from './message-markdown'
import { GetPremiumMarkdownButton } from './get-premium-markdown-button'
import { VoiceMemoMessage } from './voice-memo-message'
import { findPremiumCtaMarkers } from '@/lib/messages/premium-cta-tags'
import { useCopyToClipboard } from '@/lib/hooks/use-copy-to-clipboard'
import {
  CHAT_HIGHLIGHT_OPEN_EVENT,
  type ChatHighlight,
  type ChatHighlightDraft,
  type ChatHighlightOpenDetail
} from '@/lib/chat/chat-highlights'
import { ExtendedMessage } from '@/types/messages'
import { REGEX_NOTE } from '@/lib/preview'
import { parseInteractiveEncoding } from '@/lib/nexus/parse-interactive-encoding'
import { UserQuestionnaire } from '@/components/chat/user-questionnaire'
import { SuggestedResponses } from '@/components/chat/suggested-responses'
import type { UserQuestionnaire as UserQuestionnairePayload } from '@/types/user-questionnaire'
import { toSpeakableText } from '@/lib/speakable-text'
import { parseSuggestions } from '@/lib/utils/parse-suggestions'
import {
  parseSidekickSpaceItemTags,
  serializeSidekickSpaceItemTag,
  stripSidekickSpaceItemTags
} from '@/lib/sidekick/space-item-context'
import {
  FormComponent,
  ScheduleCalendarComponent,
  ScheduleTimeComponent
} from '@/components/embed/nexus-interactive-suggestions'
import { ChatOptionsConfirm } from '@/components/messages/chat-options-confirm'
import { ReactionTarget, type ReactionTargetType } from '@/components/reactions/reaction-target'
import type { InteractiveSuggestion } from '@/types/nexus-interactive-suggestions'
import { ResilientImage } from '@/components/ui/resilient-image'
import { ImageModal } from '@/components/ui/image-modal'
import { CitationSourcesList } from '@/components/messages/citation-chip'
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning'
import { ThinkingTranscript } from '@/components/ui/ai-thinking-block'
import { CollapsibleContent } from '@/components/ui/collapsible'
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader
} from '@/components/ai-elements/chain-of-thought'
import { ArrowRightCircle, CheckCircle2, Circle } from 'lucide-react'
import {
  extractTrailingCitationSources,
  mergeCitationSources,
  parseCitationSources
} from '@/lib/citations'
import { sanitizeAssistantVisibleContent } from '@/lib/chat/assistant-visible-content'
import { mergeLosiSteps, normalizeLosiSteps } from '@/lib/chat/losi-steps-merge'
import {
  getVoiceMemoVisibleText,
  mergeVoiceMemoMetadata,
  readVoiceMemoMetadata,
  updateChatVoiceMemoTranscript
} from '@/lib/chat/voice-memo'
import { rememberVoiceNoteCorrection } from '@/lib/chat/voice-note-dictionary'
import { getReadableFileItemContent } from '@/lib/files/file-item-content'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

const ICON_SIZE = 32
const MESSAGE_ANNOTATION_STACK_CLASS = 'not-prose flex w-full flex-col gap-2'
const MESSAGE_ANNOTATION_ITEM_CLASS = 'flex w-full flex-col gap-2'
const SIDECAR_ARTIFACT_PATH_PREFIX = 'artifacts/sidecar/'
const VIRTUAL_FILE_CREATED_KEYS = [
  'VirtualFilesystem__losi__fs_write_file',
  'VirtualFilesystem__losi__fs_patch_file'
]
const animatedOptimisticMessageKeys = new Set<string>()

type ResolvedMessageImagesState = {
  sourceKey: string
  urls: string[]
}

// Warms the browser image cache before a resolved URL reaches <ResilientImage>,
// so swapping a blob: preview for the persisted signed URL doesn't visibly
// reload (spinner + fade-in) once the bytes are already local.
const preloadImageUrl = (url: string) =>
  new Promise<string>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(url)
      return
    }

    const image = new window.Image()
    image.onload = () => resolve(url)
    image.onerror = () => resolve(url)
    image.src = url
  })

const getMessageMetadata = (metadata: Json | null) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null
  }

  return metadata as Record<string, Json>
}

const readNudgeFirstReadAnimation = (metadata: Record<string, Json> | null) => {
  if (metadata?.source !== 'nexus_nudges') {
    return false
  }

  const nudge = metadata.nudge
  if (!nudge || typeof nudge !== 'object' || Array.isArray(nudge)) {
    return false
  }

  const readReceipt = (nudge as Record<string, unknown>).readReceipt
  if (
    !readReceipt ||
    typeof readReceipt !== 'object' ||
    Array.isArray(readReceipt)
  ) {
    return false
  }

  const receipt = readReceipt as Record<string, unknown>

  return (
    receipt.table === 'chat_read_states' &&
    receipt.firstOpenAnimation === 'stream'
  )
}

const readChatContextCompaction = (metadata: Record<string, Json> | null) => {
  const compaction = metadata?.chatContextCompaction

  if (!compaction || typeof compaction !== 'object' || Array.isArray(compaction)) {
    return null
  }

  const record = compaction as Record<string, unknown>
  const summary = typeof record.summary === 'string' ? record.summary.trim() : ''

  if (!summary) {
    return null
  }

  return {
    status: typeof record.status === 'string' ? record.status : null,
    summary,
    droppedMessageCount:
      typeof record.droppedMessageCount === 'number'
        ? record.droppedMessageCount
        : null
  }
}

const readSpeechErrorMessage = async (response: Response) => {
  try {
    const payload = (await response.clone().json()) as { error?: unknown }
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim()
    }
  } catch {
    // Fall through to text/status fallback.
  }

  try {
    const text = await response.text()
    if (text.trim()) return text.trim()
  } catch {
    // Fall through to status fallback.
  }

  return `Text-to-speech failed with status ${response.status}`
}

const buildVoiceMemoTranscriptContent = (
  content: string,
  previousVoiceMemo: NonNullable<ReturnType<typeof readVoiceMemoMetadata>>,
  nextTranscript: string
) => {
  return [getVoiceMemoVisibleText(content, previousVoiceMemo).trim(), nextTranscript.trim()]
    .filter(Boolean)
    .join('\n\n')
}

type ReasoningSummaryPayload = {
  text?: string
  incomplete?: boolean
  reason?: string
  duration_seconds?: number
}

type ReasoningSummaryRenderData = ReasoningSummaryPayload & {
  isStreamingSummary: boolean
}

type ReasoningSection = {
  body: string[]
  key: string | null
  title: string | null
}

const parseReasoningSummaryPayload = (payload: unknown): ReasoningSummaryPayload | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }

  const record = payload as Record<string, unknown>
  const text = typeof record.text === 'string' ? record.text : undefined
  if (!text) return null

  return {
    text,
    incomplete: record.incomplete === true,
    reason: typeof record.reason === 'string' ? record.reason : undefined,
    duration_seconds: typeof record.duration_seconds === 'number' ? record.duration_seconds : undefined
  }
}

const getReasoningSummaryPayload = (
  annotation: Annotation | Annotation2,
  key: 'reasoning_summary' | 'reasoning_summary_stream'
): ReasoningSummaryPayload | null => {
  return parseReasoningSummaryPayload((annotation as Record<string, unknown>)[key])
}

/**
 * Resolve the per-tool annotation KEY (e.g. `webScraper__googleSearch`) that
 * corresponds to a render-segment `toolId` (e.g.
 * `platform-webScraper__googleSearch-1737059123`). Tool results are persisted
 * under these per-tool keys; the `tool_timeline` array is frequently empty, so
 * inline tool segments must fall back to reading the result from here or they
 * render blank once the message finalizes (results "vanish at Complete").
 */
function resolveToolAnnotationKeyForSegment(
  annotation: Record<string, unknown> | null,
  toolId: string | undefined
): string | null {
  if (!annotation || !toolId) return null

  // Exact match first (some ids ARE the annotation key).
  if (toolId in annotation) return toolId

  // Strip a leading `platform-` prefix and a trailing `-<digits>` id suffix,
  // which is how streamed tool ids are formatted around the tool name.
  const stripped = toolId.replace(/^platform-/, '').replace(/-\d+$/, '')
  if (stripped && stripped in annotation) return stripped

  // Last resort: find an annotation key that the toolId contains (handles
  // extra prefixes/suffixes) and that actually carries a tool result.
  const match = Object.keys(annotation).find(
    (key) => key.includes('__') && (toolId === key || toolId.includes(key))
  )
  return match ?? null
}

function readLosiCodeAnnotationPayload(
  annRecord: Record<string, unknown>,
  key: string
): Record<string, unknown> | null {
  const raw = annRecord[key]
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const wrapped = raw as { result?: unknown }
  if (wrapped.result && typeof wrapped.result === 'object' && !Array.isArray(wrapped.result)) {
    return wrapped.result as Record<string, unknown>
  }
  return raw as Record<string, unknown>
}

/**
 * Multiple losiCode__* tools in one turn each register an annotation key.
 * Without this filter, chat paints a Connector Request / Losi Code card per
 * key (request_connector + check_readiness, start_coding_run + get_status).
 */
function shouldSkipDuplicateLosiCodeAnnotation(
  key: string,
  annRecord: Record<string, unknown>
): boolean {
  if (!key.startsWith('losiCode__')) return false

  const hasRequestConnector = annRecord.losiCode__request_connector != null
  const hasCheckReadiness = annRecord.losiCode__check_coding_readiness != null
  const hasStart = annRecord.losiCode__start_coding_run != null
  const hasGetStatus = annRecord.losiCode__get_coding_run_status != null
  const hasVerification = annRecord.losiCode__run_verification != null

  const startPayload = hasStart
    ? readLosiCodeAnnotationPayload(annRecord, 'losiCode__start_coding_run')
    : null
  const startNeedsConnector = Boolean(
    startPayload &&
      (startPayload.needsConnector === true ||
        (Array.isArray(startPayload.missingConnectors) &&
          startPayload.missingConnectors.length > 0))
  )
  const startHasLiveRun = Boolean(
    startPayload &&
      !startNeedsConnector &&
      (typeof startPayload.runId === 'string' ||
        typeof startPayload.taskId === 'string')
  )

  // One connector card: request_connector > check_coding_readiness >
  // start_coding_run(needsConnector).
  if (key === 'losiCode__check_coding_readiness' && hasRequestConnector) {
    return true
  }
  if (key === 'losiCode__start_coding_run' && startNeedsConnector) {
    if (hasRequestConnector || hasCheckReadiness) return true
  }

  // One live Losi Code canvas when start already owns the run.
  if (
    (key === 'losiCode__get_coding_run_status' ||
      key === 'losiCode__ensure_coding_task' ||
      key === 'losiCode__run_verification') &&
    startHasLiveRun
  ) {
    return true
  }

  // ensure_coding_task alone dumps a "Result summary" JSON card — skip when
  // any richer losiCode surface is already present.
  if (
    key === 'losiCode__ensure_coding_task' &&
    (hasStart || hasGetStatus || hasVerification || hasRequestConnector)
  ) {
    return true
  }

  if (key === 'losiCode__get_coding_run_status' && hasVerification) {
    return true
  }

  return false
}

/**
 * Multiple tool keys in one turn can map to the SAME rich card component.
 * Without this filter, chat paints one card per key (connector×2, slides×N,
 * live scores×2, etc.). Prefer one surface per family.
 */
function shouldSkipDuplicateRichAnnotation(
  key: string,
  annRecord: Record<string, unknown>
): boolean {
  if (shouldSkipDuplicateLosiCodeAnnotation(key, annRecord)) {
    return true
  }

  const has = (candidate: string) => annRecord[candidate] != null

  // liveScores: LiveScoreAnnotation reads BOTH payloads when toolKey is
  // omitted — mounting once per key doubled every card. Keep one mount.
  if (key === 'liveScores__getLiveScores' && has('liveScores__watchGame')) {
    return true
  }

  // Private vault login: same CredentialVaultLoginRequestAnnotation.
  if (
    key === 'computerUse__requestCredential' &&
    (has('CredentialVault__requestLogin') || has('CredentialVault__requestSecret'))
  ) {
    return true
  }
  if (
    key === 'CredentialVault__requestSecret' &&
    has('CredentialVault__requestLogin')
  ) {
    return true
  }

  // Deep research / platformTools share DeepResearchAnnotation — one mount.
  const deepResearchKeys = Object.keys(annRecord).filter(
    (k) => k === 'platformTools' || k === 'deepResearch' || k.startsWith('deepResearch')
  )
  if (deepResearchKeys.includes(key) && deepResearchKeys.length > 1) {
    const preferred =
      deepResearchKeys.find((k) => k === 'deepResearch') ??
      deepResearchKeys.find((k) => k.startsWith('deepResearch')) ??
      deepResearchKeys.find((k) => k === 'platformTools') ??
      deepResearchKeys[0]
    return key !== preferred
  }

  // slideCreator: AnnotationPresentation walks all keys for the first url —
  // one mount is enough.
  if (key.startsWith('slideCreator__')) {
    const slideKeys = Object.keys(annRecord).filter((k) =>
      k.startsWith('slideCreator__')
    )
    if (slideKeys.length <= 1) return false
    const preferred =
      slideKeys.find((k) => k === 'slideCreator__exportPresentation') ??
      slideKeys.find((k) => k === 'slideCreator__createPresentation') ??
      slideKeys[0]
    return key !== preferred
  }

  // taskAutomation: Task annotation already picks the first matching key.
  if (key.startsWith('taskAutomation__')) {
    const taskKeys = Object.keys(annRecord).filter((k) =>
      k.startsWith('taskAutomation__')
    )
    if (taskKeys.length <= 1) return false
    const preferred =
      taskKeys.find((k) => k === 'taskAutomation__createTask') ??
      taskKeys.find((k) => k === 'taskAutomation__createInstantTask') ??
      taskKeys.find((k) => k === 'taskAutomation__editTask') ??
      taskKeys.find((k) => k === 'taskAutomation__runTask') ??
      taskKeys.find((k) => k === 'taskAutomation__scheduleTask') ??
      taskKeys.find((k) => k === 'taskAutomation__listTasks') ??
      taskKeys[0]
    return key !== preferred
  }

  // Generated integration: prefer the created/updated card over the draft.
  if (key.startsWith('GeneratedIntegrationTool__')) {
    const genKeys = Object.keys(annRecord).filter((k) =>
      k.startsWith('GeneratedIntegrationTool__')
    )
    if (genKeys.length <= 1) return false
    const preferred =
      genKeys.find(
        (k) => k === 'GeneratedIntegrationTool__approveIntegrationDraft'
      ) ??
      genKeys.find((k) => k === 'GeneratedIntegrationTool__updateIntegration') ??
      genKeys.find((k) => k === 'GeneratedIntegrationTool__deleteIntegration') ??
      genKeys.find((k) => k === 'GeneratedIntegrationTool__draftIntegration') ??
      genKeys[0]
    return key !== preferred
  }

  return false
}


const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isDatabaseUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

const getFreePlanToolLimitCtaPayload = (annotation: Annotation | Annotation2): string | null => {
  const annotationRecord = annotation as Record<string, unknown>
  const payload = annotationRecord.free_plan_tool_limit ?? annotationRecord.model_switch
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null

  const cta = (payload as Record<string, unknown>).cta
  if (typeof cta !== 'string') return null

  return findPremiumCtaMarkers(cta)[0]?.payloadText ?? cta
}

const readRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const collectVirtualFilePaths = (value: unknown) => {
  const record = readRecord(value)

  if (!record) return []

  const result = readRecord(record.result) ?? record
  const paths: string[] = []

  if (typeof result.path === 'string') {
    paths.push(result.path)
  }

  if (Array.isArray(result.files)) {
    result.files.forEach((file) => {
      const fileRecord = readRecord(file)

      if (typeof fileRecord?.path === 'string') {
        paths.push(fileRecord.path)
      }
    })
  }

  return paths
}

const isSidecarReportVirtualFileAnnotation = (
  annotation: Record<string, unknown>
) => {
  if (!readRecord(annotation.sidecar_run)) {
    return false
  }

  const paths = VIRTUAL_FILE_CREATED_KEYS.flatMap((key) =>
    collectVirtualFilePaths(annotation[key])
  )

  return (
    paths.length > 0 &&
    paths.every((path) => path.startsWith(SIDECAR_ARTIFACT_PATH_PREFIX))
  )
}

const hasReasoningSummaryText = (value: unknown) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const record = value as Record<string, unknown>
  return Boolean(
    getReasoningSummaryPayload(record as Annotation, 'reasoning_summary') ||
      getReasoningSummaryPayload(record as Annotation, 'reasoning_summary_stream')
  )
}

const isReasoningSectionTitle = (line: string, nextLine?: string) => {
  const trimmed = line.trim()
  const nextTrimmed = nextLine?.trim()
  if (!trimmed || !nextTrimmed) return false
  if (trimmed.length > 72) return false
  if (trimmed.split(/\s+/).length > 8) return false
  if (/^[#>*\-+\d.)]/.test(trimmed)) return false
  if (/[.!?,;:]$/.test(trimmed)) return false
  if (
    /^(i|i'm|i'll|i(?:'|\u2019)d|i(?:'|\u2019)ve|let|let's|this|that|it|the|a|an|we|they|user)\b/i.test(
      trimmed
    )
  ) {
    return false
  }

  return true
}

const getReasoningSectionKey = (title: string) => title.trim().toLowerCase().replace(/\s+/g, ' ')

const stringifyReasoningSection = (section: ReasoningSection) => {
  const body = section.body.join('\n').trim()
  if (!section.title) return body
  return body ? `${section.title}\n${body}` : section.title
}

const parseReasoningSections = (text: string) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const sections: ReasoningSection[] = []
  let current: ReasoningSection | null = null

  const pushCurrent = () => {
    if (!current) return

    const hasTitle = Boolean(current.title?.trim())
    const hasBody = current.body.some((line) => line.trim())
    if (hasTitle || hasBody) {
      sections.push(current)
    }
  }

  lines.forEach((line, index) => {
    if (isReasoningSectionTitle(line, lines[index + 1])) {
      pushCurrent()
      const title = line.trim()
      current = {
        body: [],
        key: getReasoningSectionKey(title),
        title
      }
      return
    }

    if (!current) {
      current = {
        body: [],
        key: null,
        title: null
      }
    }

    current.body.push(line)
  })

  pushCurrent()
  return sections
}

const dedupeReasoningSummaryText = (text: string) => {
  const sections = parseReasoningSections(text)
  if (sections.length === 0) return text.trim()

  const sectionIndexes = new Map<string, number>()
  const dedupedSections: ReasoningSection[] = []

  for (const section of sections) {
    if (!section.key) {
      dedupedSections.push(section)
      continue
    }

    const existingIndex = sectionIndexes.get(section.key)
    if (existingIndex === undefined) {
      sectionIndexes.set(section.key, dedupedSections.length)
      dedupedSections.push(section)
      continue
    }

    const existingSection = dedupedSections[existingIndex]
    const existingBody = existingSection.body.join('\n').trim()
    const nextBody = section.body.join('\n').trim()

    if (!nextBody || existingBody.includes(nextBody)) {
      continue
    }

    dedupedSections[existingIndex] = {
      ...section,
      body:
        nextBody.startsWith(existingBody) || nextBody.length >= existingBody.length
          ? section.body
          : existingSection.body
    }
  }

  return dedupedSections.map(stringifyReasoningSection).filter(Boolean).join('\n\n').trim()
}

const mergeReasoningSummaryText = (currentText: string, nextText: string) => {
  const current = dedupeReasoningSummaryText(currentText)
  const next = dedupeReasoningSummaryText(nextText)

  if (!current || next.startsWith(current)) {
    return next
  }

  if (current.startsWith(next) || current.includes(next)) {
    return current
  }

  return dedupeReasoningSummaryText(`${current}\n\n${next}`)
}

const getReasoningSummaryRenderData = (
  annotation: Annotation | Annotation2,
  isGenerating: boolean
): ReasoningSummaryRenderData | null => {
  const streamSummary = getReasoningSummaryPayload(annotation, 'reasoning_summary_stream')
  if (isGenerating && streamSummary) {
    return {
      ...streamSummary,
      isStreamingSummary: true
    }
  }

  const finalSummary = getReasoningSummaryPayload(annotation, 'reasoning_summary')
  if (!finalSummary) {
    return streamSummary
      ? {
          ...streamSummary,
          isStreamingSummary: false
        }
      : null
  }

  return {
    ...finalSummary,
    isStreamingSummary: false
  }
}

const getReasoningSummaryText = (summary: ReasoningSummaryPayload | null) => {
  if (!summary?.text) {
    return ''
  }

  const text =
    summary.incomplete && summary.reason
      ? `${summary.text}\n\n_Reasoning incomplete: ${summary.reason}_`
      : summary.text

  return dedupeReasoningSummaryText(text)
}

const ReasoningSummaryBlock: FC<{
  summary: ReasoningSummaryRenderData
  isGenerating?: boolean
}> = ({ summary, isGenerating = false }) => {
  const summaryText = getReasoningSummaryText(summary)
  const [displayText, setDisplayText] = useState(summaryText || '')

  useEffect(() => {
    if (!summaryText) {
      return
    }

    // Stream payloads are cumulative (or replace). Never append onto local
    // state when the incoming text already supersedes it — that concatenated
    // Thought text after tool rounds (same paragraphs twice).
    setDisplayText((currentText) => {
      if (!currentText) return summaryText
      if (summaryText === currentText) return currentText
      if (summaryText.startsWith(currentText) || currentText.includes(summaryText)) {
        return summaryText.startsWith(currentText) ? summaryText : currentText
      }
      if (currentText.startsWith(summaryText)) return currentText
      return mergeReasoningSummaryText(currentText, summaryText)
    })
  }, [summaryText])

  if (!summaryText) return null

  const isStreaming = summary.isStreamingSummary && isGenerating

  return (
    <Reasoning className="w-full" defaultOpen={isStreaming} isStreaming={isStreaming} duration={summary.duration_seconds}>
      <ReasoningTrigger />
      <CollapsibleContent className="mt-2 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
        <ThinkingTranscript content={displayText} isStreaming={isStreaming} />
      </CollapsibleContent>
    </Reasoning>
  )
}

const ContinuousReasoningBlock: FC<{
  content: string
  isStreaming?: boolean
}> = ({ content, isStreaming = false }) => {
  const trimmedContent = content.trim()
  if (!trimmedContent) return null

  return (
    <Reasoning className="my-3 w-full min-w-0 max-w-full" defaultOpen={isStreaming} isStreaming={isStreaming}>
      <ReasoningTrigger />
      <ReasoningContent animateStreaming={false}>{trimmedContent}</ReasoningContent>
    </Reasoning>
  )
}

type MessageContentParts = {
  thinkingContent: string | null
  responseContent: string
}

/**
 * Display-only: re-insert a paragraph break at "glue" points where a sentence
 * that ended right before a tool call was concatenated with the sentence that
 * resumed after it, with no separator — e.g. "...right now.Good first pass.",
 * "...at once 🔧All three tools...", "...clean ✅ Here's what came back:".
 *
 * This happens because tool boundaries (render_segments/tool_timeline) are
 * stripped before persistence, so reloaded content has the two runs joined.
 * We fix it at render so it works for BOTH old and new messages and across
 * every server path, without mutating stored content or segment offsets.
 *
 * Conservative by design — it only splits when ALL hold:
 *  - left char is a sentence terminator (. ! ?) OR a checkmark/emoji sentinel
 *  - immediately (no whitespace) followed by an uppercase letter or emoji that
 *    starts a new clause
 *  - not inside a code span/fence (guarded by skipping when a backtick run is
 *    open) and not a decimal number (e.g. "3.5") or a known abbreviation.
 */
const normalizeGluedSentences = (content: string): string => {
  if (!content || content.includes('```')) return content
  return content
    // sentence-terminator directly followed by a new sentence (no space)
    .replace(/([.!?])([A-Z][a-z])/g, (_m, term: string, next: string) => `${term}\n\n${next}`)
    // an emoji/pictograph directly followed by an uppercase letter (e.g. "🔧All")
    .replace(
      /(\p{Extended_Pictographic}\uFE0F?)(\p{Lu})/gu,
      (_m, emoji: string, next: string) => `${emoji}\n\n${next}`
    )
    // a colon ending a prose clause, glued to a new capitalized sentence
    // (e.g. "...folder:Good", "...parallel:Now"). Guarded so it does NOT touch
    // URLs (://), times (10:30 — left is a digit), or spaced "Label: Value".
    .replace(/([a-z]):([A-Z][a-z])/g, (_m, left: string, next: string) => `${left}:\n\n${next}`)
}

const processMessageContent = (content: string): MessageContentParts => {
  const startTag = '<think>'
  const endTag = '</think>'
  const thinkingParts: string[] = []
  const responseParts: string[] = []
  let cursor = 0

  while (cursor < content.length) {
    const startIndex = content.indexOf(startTag, cursor)
    if (startIndex === -1) {
      responseParts.push(content.slice(cursor))
      break
    }

    responseParts.push(content.slice(cursor, startIndex))

    const thinkingStart = startIndex + startTag.length
    const endIndex = content.indexOf(endTag, thinkingStart)
    if (endIndex === -1) {
      thinkingParts.push(content.slice(thinkingStart))
      cursor = content.length
      break
    }

    thinkingParts.push(content.slice(thinkingStart, endIndex))
    cursor = endIndex + endTag.length
  }

  const thinkingContent = thinkingParts
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n')

  // Response text can be split by one or more <think>…</think> blocks. Joining
  // the surrounding pieces with '' glues sentences together
  // ("...right now.Good first pass."). Join non-empty pieces with a paragraph
  // break so each becomes its own block. This only changes output when inline
  // <think> tags are present (a single-run message is a no-op) — and
  // tool-driven messages that use render_segments do not carry inline <think>,
  // so the segmented slicer's offset basis is unaffected.
  const responseContent = responseParts
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n')
    .trim()

  return {
    thinkingContent: thinkingContent || null,
    responseContent
  }
}

const LosiStepsChainOfThought: FC<{ steps: ChatTodoItem[] }> = ({ steps }) => {
  const orderedSteps = [...steps].sort((first, second) => first.order - second.order)
  const doneCount = orderedSteps.filter((step) => step.status === 'done').length

  return (
    <ChainOfThought className="w-full" defaultOpen>
      <ChainOfThoughtHeader>
        Losi steps {doneCount}/{orderedSteps.length}
      </ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {orderedSteps.map((step) => (
          <div
            key={step.id}
            className={cn(
              'flex gap-2 py-1.5 text-sm',
              step.status === 'done' && 'text-muted-foreground',
              step.status === 'in_progress' && 'text-foreground',
              step.status === 'pending' && 'text-muted-foreground/70'
            )}
          >
            <span
              className={cn(
                'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border',
                step.status === 'done' &&
                  'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-emerald-950',
                step.status === 'in_progress' &&
                  'border-primary bg-primary text-primary-foreground',
                step.status === 'pending' &&
                  'border-muted-foreground/50 bg-background text-muted-foreground'
              )}
              aria-label={step.status.replace(/_/g, ' ')}
            >
              {step.status === 'done' ? (
                <CheckCircle2 className="size-3.5" />
              ) : step.status === 'in_progress' ? (
                <ArrowRightCircle className="size-3.5" />
              ) : (
                <Circle className="size-3" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className={cn('block font-medium', step.status === 'done' && 'line-through')}>
                {step.label}
              </span>
            </span>
          </div>
        ))}
      </ChainOfThoughtContent>
    </ChainOfThought>
  )
}

// interface ExtendedMessage extends Tables<"messages"> {
//   citations?: string
//   sharing?: string
//   message: Tables<"messages">
//   fileItems: string[]
// }

interface MessageProps {
  showActions?: boolean
  assistants: Tables<'assistants'>[]
  codeBlocks?: ChatMessageCodeBlock[]
  profile: Tables<'profiles'> | null
  allModels: LLM[]
  selectedAssistant: Tables<'assistants'> | null
  files: Tables<'files'>[]
  message: ExtendedMessage
  fileItems: Tables<'file_items'>[]
  highlights?: ChatHighlight[]
  onCreateHighlight?: (highlight: ChatHighlightDraft) => void | Promise<void>
  isEditing: boolean
  isLast: boolean
  onStartEdit?: (message: ExtendedMessage | Tables<'messages'>) => void
  onCancelEdit?: () => void
  onSubmitEdit?: (value: string, sequenceNumber: number) => void
  onRegenerate?: (request?: RegenerateRequest) => void
  isGenerating: boolean
  firstTokenReceived: boolean
  isActiveAssistantTurn?: boolean
  setIsGenerating?: (value: boolean) => void
  onSelectCodeBlock?: (codeBlock: ChatMessageCodeBlock | null) => void
  isExperimentalCodeEditor?: boolean
  showResponseTime?: boolean
  ref?: React.RefObject<HTMLDivElement> | null
  className?: string
  isSelectionMode?: boolean
  isSelected?: boolean
  onSelect?: (messageId: string) => void
  citations?: string
  onSelectMessage?: (message: any) => void
  isPublic?: boolean
  previousUserMessageContent?: string | null
  /** When scrolling from a quote click, the quoted text to highlight in this message */
  highlightQuoteText?: string
  /**
   * Narrow Sidekick panel layout only: keeps own-user bubbles left-aligned
   * (no justify-end / items-end / pl-* insets). Does not change MessageActions
   * placement — those already use footer position. Space conversation chat
   * must leave this false/undefined so it matches main chat alignment.
   */
  isSidekick?: boolean
  reactionTargetType?: ReactionTargetType | null
  /** When user confirms a form/date/time suggestion, send this as the next user message (main chat) */
  onConfirmInteractive?: (payload: string) => void
}

export const Message = forwardRef<HTMLDivElement, MessageProps>(
  (
    {
      isGenerating,
      firstTokenReceived,
      isActiveAssistantTurn = false,
      setIsGenerating,
      assistants,
      message,
      fileItems,
      isEditing,
      isLast,
      onStartEdit,
      onCancelEdit,
      onRegenerate,
      onSubmitEdit,
      onSelectCodeBlock,
      codeBlocks,
      profile,
      allModels,
      selectedAssistant,
      files,
      isExperimentalCodeEditor,
      showResponseTime = false,
      showActions = true,
      highlights = [],
      onCreateHighlight,
      className,
      isSelectionMode,
      isSelected,
      onSelect,
      citations,
      onSelectMessage,
      isPublic = false,
      previousUserMessageContent = null,
      highlightQuoteText,
      isSidekick = false,
      reactionTargetType = null,
      onConfirmInteractive
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { navigateToHref } = useSplitPaneNavigation()
    const prefersReducedMotion = useReducedMotion()

    // State to track if long messages are expanded
    const [isExpanded, setIsExpanded] = useState(false)
    const citationSources = useMemo(
      () => parseCitationSources(message.citations),
      [message.citations]
    )

    // Constants for message truncation
    const MESSAGE_TRUNCATE_LENGTH = 500 // Characters to show before truncating
    const MESSAGE_TRUNCATE_LINES = 10 // Lines to show before truncating

    useEffect(() => {
      if (message.role !== 'user') return

      const handleHighlightOpen = (event: Event) => {
        const detail = (event as CustomEvent<ChatHighlightOpenDetail>).detail
        if (detail?.messageId === message.id) {
          setIsExpanded(true)
        }
      }

      window.addEventListener(CHAT_HIGHLIGHT_OPEN_EVENT, handleHighlightOpen)
      return () => {
        window.removeEventListener(CHAT_HIGHLIGHT_OPEN_EVENT, handleHighlightOpen)
      }
    }, [message.id, message.role])

    // Hook for copying to clipboard
    const { copyToClipboard } = useCopyToClipboard({ timeout: 2000 })

    const editInputRef = useRef<HTMLTextAreaElement>(null)
    const messageText = typeof message.content === 'string' ? message.content : ''
    const messageMetadata = useMemo(() => getMessageMetadata(message.metadata), [message.metadata])
    const inlineQuestionnaire = useMemo(() => {
      const raw = messageMetadata?.questionnaire
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw as unknown as UserQuestionnairePayload
      }
      if (typeof message.content !== 'string' || !message.content) return null
      return parseSuggestions(message.content).questionnaire
    }, [message.content, messageMetadata])
    const inlineSuggestions = useMemo(() => {
      if (inlineQuestionnaire) return [] as string[]
      const fromMeta = messageMetadata?.suggestions
      if (Array.isArray(fromMeta) && fromMeta.length > 0) {
        return fromMeta.filter(
          (item): item is string => typeof item === 'string' && item.trim().length > 0
        )
      }
      if (typeof message.content !== 'string' || !message.content) return [] as string[]
      return parseSuggestions(message.content).suggestions
    }, [inlineQuestionnaire, message.content, messageMetadata])
    const voiceMemo = useMemo(() => readVoiceMemoMetadata(messageMetadata), [messageMetadata])
    const nudgeFirstReadAnimationEnabled = useMemo(
      () => readNudgeFirstReadAnimation(messageMetadata),
      [messageMetadata]
    )

    const [editedMessage, setEditedMessage] = useState(messageText)
    const [thumbsUp, setThumbsUp] = useState<boolean | null>(message.thumbs_up ?? null)
    const [isStarred, setIsStarred] = useState(
      (message as any).starred === true
    )
    const [animateNudgeFirstRead, setAnimateNudgeFirstRead] = useState(false)
    const nudgeFirstReadRequestKeyRef = useRef<string | null>(null)

    useEffect(() => {
      if (
        isPublic ||
        message.role !== 'assistant' ||
        !nudgeFirstReadAnimationEnabled ||
        !message.id ||
        !message.chat_id ||
        !isDatabaseUuid(message.id) ||
        !isDatabaseUuid(message.chat_id) ||
        isGenerating ||
        prefersReducedMotion
      ) {
        setAnimateNudgeFirstRead(false)
        return
      }

      const requestKey = `${message.chat_id}:${message.id}`
      if (nudgeFirstReadRequestKeyRef.current === requestKey) {
        return
      }

      nudgeFirstReadRequestKeyRef.current = requestKey
      let cancelled = false

      void authenticatedFetch(
        `/api/chat/${encodeURIComponent(message.chat_id)}/read-state`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            messageId: message.id
          })
        }
      )
        .then(async (response) => {
          if (!response.ok) return null
          return response.json() as Promise<{ firstRead?: unknown }>
        })
        .then((payload) => {
          if (!cancelled && payload?.firstRead === true) {
            setAnimateNudgeFirstRead(true)
          }
        })
        .catch(() => undefined)

      return () => {
        cancelled = true
      }
    }, [
      isGenerating,
      isPublic,
      message.chat_id,
      message.id,
      message.role,
      nudgeFirstReadAnimationEnabled,
      prefersReducedMotion
    ])

    useEffect(() => {
      if (!animateNudgeFirstRead) {
        return
      }

      const duration = Math.min(
        2600,
        Math.max(900, Math.ceil(messageText.length * 12))
      )
      const timeoutId = window.setTimeout(() => {
        setAnimateNudgeFirstRead(false)
      }, duration)

      return () => window.clearTimeout(timeoutId)
    }, [animateNudgeFirstRead, messageText.length])

    // Listen for message update events from tools
    useEffect(() => {
      const unsubscribe = chatUpdateManager.subscribe((event) => {
        if (
          event.data.messageId === message.id &&
          (event.type === 'message-starred' || event.type === 'message-unstarred')
        ) {
          // Update local thumbsUp state to reflect the change
          setThumbsUp(event.data.thumbsUp ?? null)
        }
      })

      return unsubscribe
    }, [message.id])

    // Store extracted components separately to preserve them during editing
    const [extractedComponents, setExtractedComponents] = useState<string>('')
    const [mainContent, setMainContent] = useState<string>('')

    // Helper function to ensure specialized component markers are preserved for editing
    const getEditableContent = useCallback(
      (content: string, codeBlocks: ChatMessageCodeBlock[] = []) => {
        // First check for quotes and extract them
        const quoteMatch = content.match(/\[QUOTE:(.*?)\](.*?)\[\/QUOTE\]/s)
        let quoteContent = ''
        let contentWithoutQuote = content

        // If we found a quote, extract it and remove it from the main content
        if (quoteMatch) {
          quoteContent = quoteMatch[0]
          contentWithoutQuote = content.replace(/\[QUOTE:.*?\].*?\[\/QUOTE\]\n?/s, '')
        }

        const { items: spaceItemContexts, contentWithoutTags } =
          parseSidekickSpaceItemTags(contentWithoutQuote)
        const spaceItemContent = spaceItemContexts.map(serializeSidekickSpaceItemTag).join('\n\n')

        // Check for other special component tags
        const componentTagRegex =
          /\[(LOSI_APP_PREVIEW|SPACE_ITEM_CARD|YOUTUBE_VIDEO|YOUTUBE_VIDEO_LIST|FLASHCARD|TODO|QUIZ|TIMELINE|CODE_DIFF|POLL|PROGRESS|RATING|CHECKLIST|FORM|MIND_MAP|ARGUMENT_MAP|FLOW_CHART|GANTT_CHART|DATA_DASHBOARD|SYSTEM_MAP|BUSINESS_CANVAS|TABS|DEFINITION)\](.*?)\[\/\1\]/gs

        // Extract all component tags and preserve their original format
        const componentMatches = Array.from(contentWithoutTags.matchAll(componentTagRegex))
        const componentTags = componentMatches.map((match) => match[0])

        // Remove the component tags from the content to avoid processing them
        let cleanContent = contentWithoutTags
        componentTags.forEach((tag) => {
          cleanContent = cleanContent.replace(tag, '')
        })

        // Then reconstruct code blocks in the clean content
        let processedContent = reconstructContentWithCodeBlocks(cleanContent, codeBlocks)

        // Now add back the component tags at the beginning, after the quote
        const allComponentTags = componentTags.join('\n\n')

        // Store the components and main content separately
        const extractedComponentContent = [spaceItemContent, quoteContent, allComponentTags]
          .filter(Boolean)
          .join('\n\n')

        // Return an object with the separated content
        return {
          extractedComponents: extractedComponentContent,
          mainContent: processedContent.trim()
        }
      },
      []
    )

    // Extract quote information from the message content
    const quoteInfo = useMemo(() => {
      const quoteMatch = messageText.match(/\[QUOTE:(.*?)\](.*?)\[\/QUOTE\]/s)
      if (!quoteMatch) return null

      return {
        fullQuote: quoteMatch[0],
        messageId: quoteMatch[1]?.trim() || '',
        text: quoteMatch[2] || '',
        contentWithoutQuote: messageText.replace(/\[QUOTE:.*?\].*?\[\/QUOTE\]\n?/s, '')
      }
    }, [messageText])

    /** For user messages, strip leading @ from CRM mention tokens so they render as a single chip */
    const messageContentForDisplay = useMemo(() => {
      const normalizedContent =
        message.role !== 'user'
          ? messageText
          : messageText.replace(/@(\[([^:]+):\s*([^\]]+)\]\((lead|contact|company):[^)]+\))/g, '$1')
      const visibleContent =
        message.role === 'user'
          ? getVoiceMemoVisibleText(normalizedContent, voiceMemo)
          : normalizedContent

      return message.role === 'user' ? stripSidekickSpaceItemTags(visibleContent) : visibleContent
    }, [message.role, messageText, voiceMemo])

    const messageContentParts = useMemo<MessageContentParts>(
      () =>
        message.role === 'assistant'
          ? processMessageContent(messageContentForDisplay)
          : { thinkingContent: null, responseContent: messageContentForDisplay },
      [message.role, messageContentForDisplay]
    )

    const truncatedContentForDisplay = useMemo(() => {
      if (!quoteInfo) {
        return messageContentForDisplay
      }

      const contentWithoutTags = stripSidekickSpaceItemTags(quoteInfo.contentWithoutQuote)

      if (message.role !== 'user') {
        return contentWithoutTags
      }

      return contentWithoutTags.replace(
        /@(\[([^:]+):\s*([^\]]+)\]\((lead|contact|company):[^)]+\))/g,
        '$1'
      )
    }, [quoteInfo, message.role, messageContentForDisplay])

    const interactiveParse = useMemo(() => {
      if (message.role !== 'assistant' || !messageContentParts.responseContent) return null
      return parseInteractiveEncoding(messageContentParts.responseContent)
    }, [message.role, messageContentParts.responseContent])

    const hasInteractiveSuggestions = Boolean(
      message.role === 'assistant' &&
        interactiveParse &&
        interactiveParse.suggestions.length > 0 &&
        onConfirmInteractive
    )
    const [showInteractiveSuggestions, setShowInteractiveSuggestions] = useState(false)
    const shouldShowAssistantGenerationState =
      message.role === 'assistant' &&
      isGenerating &&
      (isLast || isActiveAssistantTurn)
    const chatContextCompaction = useMemo(
      () => readChatContextCompaction(messageMetadata),
      [messageMetadata]
    )
    const compactionStatusLabel =
      chatContextCompaction?.status === 'running' && shouldShowAssistantGenerationState
        ? 'Compacting conversation'
        : 'Conversation compacted'

    useEffect(() => {
      if (!hasInteractiveSuggestions) {
        setShowInteractiveSuggestions(false)
        return
      }

      // Hold suggestion chips until streaming has fully settled to avoid
      // end-of-stream flash when persistence/state finalize.
      if (shouldShowAssistantGenerationState) {
        setShowInteractiveSuggestions(false)
        return
      }

      const timer = window.setTimeout(() => {
        setShowInteractiveSuggestions(true)
      }, 140)

      return () => window.clearTimeout(timer)
    }, [hasInteractiveSuggestions, shouldShowAssistantGenerationState])

    const effectiveAssistantContent =
      message.role === 'assistant' && interactiveParse && interactiveParse.suggestions.length > 0
        ? interactiveParse.cleanContent
        : messageContentParts.responseContent
    const sanitizedAssistantContent = useMemo(
      () =>
        message.role === 'assistant'
          ? sanitizeAssistantVisibleContent(effectiveAssistantContent)
          : effectiveAssistantContent,
      [effectiveAssistantContent, message.role]
    )
    const assistantContentWithInlineSources = useMemo(
      () =>
        message.role === 'assistant'
          ? extractTrailingCitationSources(sanitizedAssistantContent)
          : { content: sanitizedAssistantContent, sources: [] },
      [message.role, sanitizedAssistantContent]
    )
    const displayAssistantContent = assistantContentWithInlineSources.content
    // Fallback-body only: fix sentences glued across stripped tool boundaries
    // ("...right now.Good", "...at once 🔧All"). Only differs from
    // displayAssistantContent when a real glue exists, so it is a no-op for
    // normal messages. Kept separate so the segmented slicer keeps raw offsets.
    const normalizedFallbackContent = useMemo(
      () =>
        message.role === 'assistant'
          ? normalizeGluedSentences(displayAssistantContent)
          : displayAssistantContent,
      [message.role, displayAssistantContent]
    )
    const shouldRenderStreamingMarkdown = shouldShowAssistantGenerationState
    const allCitationSources = useMemo(
      () => mergeCitationSources(citationSources, assistantContentWithInlineSources.sources),
      [assistantContentWithInlineSources.sources, citationSources]
    )

    // Function to check if message needs truncation
    const needsTruncation = useCallback(() => {
      if (message.role !== 'user') return false
      if (voiceMemo) return false

      // Get content without quote for truncation calculation
      let contentToCheck = stripSidekickSpaceItemTags(
        quoteInfo ? quoteInfo.contentWithoutQuote : messageText
      )

      // Attached cards (e.g. the [TREND]{…}[/TREND] payload from the trending
      // handoff) render as their own block component, not as body text. Their
      // raw payload must NOT count toward the "Show more" length check — the
      // visible body ("More on this") is tiny even when the JSON block is huge.
      contentToCheck = contentToCheck
        .replace(/\[TREND\][\s\S]*?\[\/TREND\]/g, '')
        .trim()

      if (contentToCheck.length < MESSAGE_TRUNCATE_LENGTH) return false

      // Count newlines to determine if we should truncate
      const newlineCount = (contentToCheck.match(/\n/g) || []).length
      return (
        newlineCount >= MESSAGE_TRUNCATE_LINES || contentToCheck.length > MESSAGE_TRUNCATE_LENGTH
      )
    }, [message.role, messageText, quoteInfo, voiceMemo])

    const handleFeedbackUpdate = async (
      sessionId: string,
      modelId: string,
      providerId: string,
      messageId: string,
      isThumbsUp: boolean | null
    ) => {
      try {
        await updateMessageFeedback(sessionId, modelId, providerId, messageId, isThumbsUp)
        setThumbsUp(isThumbsUp)
        // notify local listeners for UI features like timeline indicator
        chatUpdateManager.notifyMessageStarred(messageId, isThumbsUp)
      } catch (error) {
        console.error('Error updating message feedback:', error)
      }
    }

    const handleStarUpdate = async (messageId: string, starred: boolean) => {
      try {
        const response = await fetch(`/api/messages/${encodeURIComponent(messageId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ starred })
        })
        if (!response.ok) throw new Error('Failed to update star')
        setIsStarred(starred)
        chatUpdateManager.notifyMessageStarred(messageId, starred ? true : null)
      } catch (error) {
        console.error('Error updating message star:', error)
        throw error
      }
    }

    const [showImagePreview, setShowImagePreview] = useState(false)
    const [selectedImage, setSelectedImage] = useState<MessageImage | null>(null)

    const [showFileItemPreview, setShowFileItemPreview] = useState(false)
    const [selectedFileItem, setSelectedFileItem] = useState<Tables<'file_items'> | null>(null)

    const [viewSources, setViewSources] = useState(false)

    const [isVoiceToTextPlaying, setIsVoiceToTextPlaying] = useState(false)

    const messageImagePaths = useMemo(
      () => (Array.isArray(message.image_paths) ? message.image_paths : []),
      [message.image_paths]
    )
    const messageImageSourceKey = useMemo(
      () => messageImagePaths.join('\u0000'),
      [messageImagePaths]
    )
    const immediateRenderableImages = useMemo(
      () =>
        messageImagePaths
          .map((path) => getSafeRenderableImageUrl(path))
          .filter((path): path is string => Boolean(path)),
      [messageImagePaths]
    )
    const [resolvedImages, setResolvedImages] = useState<ResolvedMessageImagesState>(() => ({
      sourceKey: messageImageSourceKey,
      urls: immediateRenderableImages
    }))
    const visibleImages =
      messageImagePaths.length === 0
        ? []
        : resolvedImages.sourceKey === messageImageSourceKey
          ? resolvedImages.urls
          // Optimistic sends swap a blob: preview path for the persisted
          // storage path once the message round-trips; that path isn't
          // itself renderable, so keep showing the still-valid previous
          // image instead of blanking it out while the signed URL resolves.
          : immediateRenderableImages.length > 0
            ? immediateRenderableImages
            : resolvedImages.urls

    const audioRef = useRef<HTMLAudioElement | null>(null)
    const audioObjectUrlRef = useRef<string | null>(null)

    const [isLoading, setIsLoading] = useState(false) // Add this line

    const [currentSection, setCurrentSection] = useState<
      'thinking' | 'reflection' | 'output' | 'verification' | null
    >(null)

    const generationStartedAt = useMemo(() => {
      const rawValue = messageMetadata?.streamStartedAt
      return typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : null
    }, [messageMetadata])

    const senderImageUrl = useMemo(() => {
      const rawValue = messageMetadata?.senderImageUrl
      return getSafeRenderableImageUrl(rawValue) ?? null
    }, [messageMetadata])

    const profileImageUrl = useMemo(
      () => getSafeRenderableImageUrl(profile?.image_url) ?? null,
      [profile?.image_url]
    )

    // A user-role message is "own" when it is the current user's (or an
    // optimistic local message without a persisted user_id). In a group chat,
    // teammates' user-role messages are NOT own — they align left, while the
    // current user's messages align right. In a 1:1 chat every user message is
    // own, so this preserves the existing right-aligned behavior.
    // Public share pages: treat every user turn as the sharer's side of the
    // transcript (right-aligned) — never as a teammate left bubble.
    const isOwnMessage =
      message.role === 'user' &&
      (isPublic ||
        !message.user_id ||
        !profile?.user_id ||
        message.user_id === profile.user_id)

    const isOtherUserMessage = message.role === 'user' && !isOwnMessage

    // Public shares must not leak the sharer's photo or display name.
    const publicSafeSenderImageUrl = isPublic ? null : senderImageUrl
    const publicSafeProfileImageUrl = isPublic ? null : profileImageUrl

    const userSenderName = useMemo(() => {
      if (message.role !== 'user') {
        return null
      }

      if (isPublic) {
        return 'User'
      }

      if (typeof message.sender_name === 'string' && message.sender_name.trim()) {
        return message.sender_name.trim()
      }

      // Keep profile identity for optimistic local messages that may not yet
      // include a persisted user_id.
      if (!message.user_id || !profile?.user_id || message.user_id === profile.user_id) {
        return profile?.display_name ?? profile?.username ?? 'You'
      }

      return 'Teammate'
    }, [
      isPublic,
      message.role,
      message.sender_name,
      message.user_id,
      profile?.display_name,
      profile?.user_id,
      profile?.username
    ])

    const parseCustomTags = (content: string) => {
      const sections = ['thinking', 'reflection', 'output', 'verification']
      let parsedContent = content

      sections.forEach((section) => {
        const regex = new RegExp(`<${section}>(.*?)</${section}>`, 'gs')
        parsedContent = parsedContent.replace(regex, (match, p1) => {
          return `<div class="custom-tag ${section}"><h4>${section.charAt(0).toUpperCase() + section.slice(1)}</h4>${p1}</div>`
        })
      })

      return parsedContent
    }

    useEffect(() => {
      if (isGenerating) {
        setCurrentSection('thinking')
      } else {
        setCurrentSection(null)
      }
    }, [isGenerating])

    // Function to update the current section based on the AI's response
    const updateSection = (content: string) => {
      if (content?.includes('<thinking>')) {
        setCurrentSection('thinking')
      } else if (content?.includes('<reflection>')) {
        setCurrentSection('reflection')
      } else if (content?.includes('<verification>')) {
        setCurrentSection('verification')
      } else if (content?.includes('<output>')) {
        setCurrentSection('output')
      }
    }

    // Call updateSection whenever the message content changes
    useEffect(() => {
      updateSection(messageText)
    }, [messageText])

    const renderLatex = (latex: string, displayMode: boolean): string => {
      return katex.renderToString(latex, {
        displayMode: displayMode,
        throwOnError: false
      })
    }

    const handleCopy = useCallback(() => {
      const md = new MarkdownIt({
        html: true,
        breaks: true,
        linkify: true
      })

      const content = reconstructContentWithCodeBlocks(messageText, codeBlocks ?? [])

      let plainTextContent = content
      let htmlContent = content

      const convertBlockLatexToText = (latex: string): string => {
        return latex
          .replace(/\\text\{([^}]+)\}/g, '$1')
          .replace(/\\cdot/g, '·')
          .replace(/\\times/g, '*')
          .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2')
          .replace(/\s+/g, ' ')
          .trim()
      }

      // Handle block LaTeX
      htmlContent = plainTextContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
        htmlContent = htmlContent.replace(match, renderLatex(latex, true))
        return convertBlockLatexToText(latex) // Convert LaTeX to text without delimiters
      })

      // Render the rest of the markdown
      htmlContent = md.render(htmlContent)

      // Use navigator clipboard API with our enhanced transformation
      if (navigator.clipboard) {
        copyToClipboard(plainTextContent, htmlContent)
      } else {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea')
        textArea.value = content
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }

      if (message.role === 'assistant') {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: 'copied',
          chat_id: message.chat_id ?? undefined,
          message_id: message.id,
          source: 'assistant_message'
        })
      }
    }, [message, codeBlocks])

    function cleanupMessageForSpeech(message: string) {
      // Strip / humanize [USER_QUESTIONNAIRE…] etc. before TTS.
      return toSpeakableText(message)
    }

    function splitTextIntoChunks(text: string, maxLength: number): string[] {
      const chunks: string[] = []
      let currentChunk = ''

      text.split(' ').forEach((word) => {
        if ((currentChunk + ' ' + word).length <= maxLength) {
          currentChunk += (currentChunk ? ' ' : '') + word
        } else {
          chunks.push(currentChunk)
          currentChunk = word
        }
      })

      if (currentChunk) {
        chunks.push(currentChunk)
      }

      return chunks
    }

    const revokeGeneratedAudioUrl = useCallback((audioUrl?: string | null) => {
      if (!audioUrl) {
        return
      }

      URL.revokeObjectURL(audioUrl)

      if (audioObjectUrlRef.current === audioUrl) {
        audioObjectUrlRef.current = null
      }
    }, [])

    const stopMessagePlayback = useCallback(() => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }

      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.removeAttribute('src')

        try {
          audioRef.current.load()
        } catch {
          // Some browser test environments do not implement load().
        }
      }

      revokeGeneratedAudioUrl(audioObjectUrlRef.current)
      setIsVoiceToTextPlaying(false)
      setIsLoading(false)
    }, [revokeGeneratedAudioUrl])

    const handleOpenAISpeech = useCallback(async (text: string) => {
      let shouldContinue = true

      try {
        stopMessagePlayback()

        setIsLoading(true)
        setIsVoiceToTextPlaying(false)

        const chunks = splitTextIntoChunks(text, 4000)
        let currentChunk = 0

        for (const chunk of chunks) {
          if (!shouldContinue) break

          // Add a small prefix space to prevent word cutoff
          const chunkWithPrefix = ' ' + chunk

          const response = await fetch('/api/text-to-speech', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: chunkWithPrefix })
          })

          if (!response.ok) {
            throw new Error(await readSpeechErrorMessage(response))
          }

          const audioBlob = await response.blob()
          const audioUrl = URL.createObjectURL(audioBlob)

          if (!audioRef.current) {
            audioRef.current = new Audio()
          }

          revokeGeneratedAudioUrl(audioObjectUrlRef.current)
          audioRef.current.src = audioUrl
          audioObjectUrlRef.current = audioUrl

          try {
            audioRef.current.load()
          } catch {
            // Some browser test environments do not implement load().
          }

          try {
            if (!shouldContinue) {
              revokeGeneratedAudioUrl(audioUrl)
              break
            }

            // Add a small delay before playing to ensure audio system is ready
            await new Promise((resolve) => setTimeout(resolve, 100))

            // Set loading false and playing true only when audio actually starts
            audioRef.current.onplay = () => {
              setIsLoading(false)
              setIsVoiceToTextPlaying(true)
            }

            await audioRef.current.play()

            // Handle last chunk
            if (currentChunk === chunks.length - 1) {
              audioRef.current.onended = () => {
                setIsVoiceToTextPlaying(false)
                revokeGeneratedAudioUrl(audioUrl)
                shouldContinue = false
              }
            } else {
              // Wait for current chunk to finish
              await new Promise((resolve) => {
                if (!audioRef.current) return resolve(null)
                audioRef.current.onended = () => {
                  revokeGeneratedAudioUrl(audioUrl)
                  resolve(null)
                }
              })
            }
            currentChunk++
          } catch (error) {
            console.error('Playback error:', error)
            revokeGeneratedAudioUrl(audioUrl)
            throw error
          }
        }
      } catch (error) {
        console.error('Speech generation error:', error)
        const errorMessage =
          error instanceof Error && error.message
            ? error.message
            : 'Failed to generate speech'
        toast.error(errorMessage)
        setIsVoiceToTextPlaying(false)
        setIsLoading(false)
        throw error
      } finally {
        if (!shouldContinue) {
          setIsVoiceToTextPlaying(false)
          setIsLoading(false)
        }
      }
    }, [revokeGeneratedAudioUrl, stopMessagePlayback])

    // Cleanup function for audio resources
    useEffect(() => {
      return () => {
        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current.removeAttribute('src')
          audioRef.current = null
        }
        revokeGeneratedAudioUrl(audioObjectUrlRef.current)
      }
    }, [revokeGeneratedAudioUrl])

    const handleVoiceToText = () => {
      if (isVoiceToTextPlaying) {
        stopMessagePlayback()
        return
      }

      const cleanedText = cleanupMessageForSpeech(messageText)
      if (!cleanedText.trim()) {
        toast.error('No text content to play')
        return
      }

      setIsLoading(true)

      void handleOpenAISpeech(cleanedText).catch(() => {
        // handleOpenAISpeech owns error state. Do not hide Copy Azure
        // failures by falling back to device speech.
      })
    }

    const handleSendEdit = useCallback(() => {
      // Combine the preserved components and the edited main content
      const finalContent = extractedComponents
        ? extractedComponents + (mainContent ? '\n\n' + mainContent : '')
        : mainContent

      if (voiceMemo) {
        rememberVoiceNoteCorrection(voiceMemo.transcript, mainContent)
      }

      onSubmitEdit?.(finalContent, message.sequence_number)
      onCancelEdit?.()
    }, [onSubmitEdit, onCancelEdit, extractedComponents, mainContent, message, voiceMemo])

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (isEditing && event.key === 'Enter' && event.metaKey) {
          handleSendEdit()
        }
      },
      [isEditing, handleSendEdit]
    )

    const handleRegenerate = useCallback(async (request?: RegenerateRequest) => {
      setIsGenerating?.(true)

      // Submit negative feedback to Not Diamond on regenerate
      if (message.session_id && message.model_id && message.provider_id) {
        try {
          await fetch('/api/notdiamond/feedback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: message.session_id,
              modelId: message.model_id,
              providerId: message.provider_id,
              messageId: message.id,
              value: false // Regenerate counts as thumbs down
            })
          })
        } catch (error) {
          console.error('Error submitting Not Diamond feedback on regenerate:', error)
          // Don't show error to user - feedback submission is best-effort
        }
      }

      onRegenerate?.(request)
    }, [onRegenerate, message.session_id, message.model_id, message.provider_id, message.id])

    const handleStartEdit = useCallback(() => {
      onStartEdit?.(message)
    }, [onStartEdit, message])
    const canEditMessage =
      isOwnMessage && Boolean(onStartEdit) && !voiceMemo

    const handleDeleteMessage = useCallback(async () => {
      // Optimistic: remove from UI instantly
      window.dispatchEvent(
        new CustomEvent('losi:message-deleted', {
          detail: { messageId: message.id, chatId: message.chat_id }
        })
      )

      try {
        const res = await fetch(`/api/messages/${message.id}/soft-delete`, {
          method: 'POST'
        })
        if (!res.ok) {
          throw new Error('Failed to delete message')
        }
        toast.success(t('Message deleted'))
      } catch {
        toast.error(t('Failed to delete message'))
      }
    }, [message.id, message.chat_id, t])

    const handleDuplicateChat = useCallback(async () => {
      try {
        const res = await fetch('/api/chat/duplicate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: message.chat_id,
            upToMessageId: message.id,
            mode: 'branch'
          })
        })
        if (!res.ok) {
          throw new Error('Failed to branch chat')
        }
        const data = (await res.json()) as { chatId: string }
        toast.success(t('Branched chat'))
        navigateToHref(`/chat/${data.chatId}`)
      } catch {
        toast.error(t('Failed to branch chat'))
      }
    }, [message.chat_id, message.id, navigateToHref, t])

    const handleVoiceMemoTranscriptSave = useCallback(
      async (nextTranscript: string) => {
        if (!voiceMemo) return

        const updatedVoiceMemo = updateChatVoiceMemoTranscript(voiceMemo, nextTranscript)
        const updatedContent = buildVoiceMemoTranscriptContent(
          messageText,
          voiceMemo,
          updatedVoiceMemo.transcript
        )
        const updatedMetadata = mergeVoiceMemoMetadata(
          message.metadata,
          updatedVoiceMemo
        )

        try {
          await updateMessage(message.id, {
            content: updatedContent,
            metadata: updatedMetadata
          })
          rememberVoiceNoteCorrection(voiceMemo.transcript, updatedVoiceMemo.transcript)
          toast.success('Transcript updated')
        } catch (error) {
          console.error('Failed to update voice memo transcript:', error)
          toast.error('Failed to update transcript')
          throw error
        }
      },
      [message.id, message.metadata, messageText, voiceMemo]
    )

    const handlePreview = useCallback(() => {
      // Set local state immediately for UI feedback
      setHasNoteLocal(true)
      // Convert message to format expected by content manager (ChatMessage)
      const messageForPreview = {
        message,
        fileItems: fileItems.map((f) => f.id),
        codeBlocks: codeBlocks || []
      }
      onSelectMessage?.(messageForPreview as ChatMessage)
    }, [onSelectMessage, message, fileItems, codeBlocks])

    const handleNavigateToMessage = useCallback((messageId: string) => {
      // Sanitize messageId to prevent XSS
      const sanitizedId = messageId.replace(/[^a-zA-Z0-9-_]/g, '')
      const messageElement = document.getElementById(`message-${sanitizedId}`)

      if (messageElement) {
        try {
          // Check if smooth scrolling is supported
          const supportsSmooth = 'scrollBehavior' in document.documentElement.style

          messageElement.scrollIntoView({
            behavior: supportsSmooth ? 'smooth' : 'auto',
            block: 'start'
          })

          // Remove any existing highlight to prevent conflicts
          messageElement.classList.remove('highlight-message')

          // Clear any existing timeout
          const existingTimeoutId = messageElement.dataset.highlightTimeout
          if (existingTimeoutId) {
            clearTimeout(parseInt(existingTimeoutId))
            delete messageElement.dataset.highlightTimeout
          }

          // Use requestAnimationFrame for better performance
          requestAnimationFrame(() => {
            messageElement.classList.add('highlight-message')

            const timeoutId = setTimeout(() => {
              if (messageElement.classList.contains('highlight-message')) {
                messageElement.classList.remove('highlight-message')
                delete messageElement.dataset.highlightTimeout
              }
            }, 2000)

            // Store timeout reference for potential cleanup
            messageElement.dataset.highlightTimeout = timeoutId.toString()
          })
        } catch (error) {
          // Navigation to message failed; ignore silently
        }
      }
    }, [])

    // Cleanup effect to handle pending timeouts on unmount
    useEffect(() => {
      return () => {
        // Clean up any pending highlight timeouts when component unmounts
        const sanitizedId = message.id.replace(/[^a-zA-Z0-9-_]/g, '')
        const messageElement = document.getElementById(`message-${sanitizedId}`)
        if (messageElement?.dataset.highlightTimeout) {
          clearTimeout(parseInt(messageElement.dataset.highlightTimeout))
          delete messageElement.dataset.highlightTimeout
        }
      }
    }, [message.id])

    // const handleStartEdit = () => {
    //   onStartEdit?.(message)
    // }

    // Initialize the edited message when the component mounts
    useEffect(() => {
      // Only reset edited message when not in edit mode
      if (!isEditing) {
        const parsedContent = getEditableContent(messageText, codeBlocks ?? [])
        setMainContent(parsedContent.mainContent)
        setExtractedComponents(parsedContent.extractedComponents)
      }
    }, [messageText, isEditing, codeBlocks, getEditableContent])

    // Use the original content for the textarea, not the processed content with component markers
    useEffect(() => {
      if (isEditing) {
        // Use our helper function to get the editable content with all component syntax preserved
        const parsedContent = getEditableContent(messageText, codeBlocks ?? [])

        // Store the extracted components and main content separately
        setExtractedComponents(parsedContent.extractedComponents)
        setMainContent(parsedContent.mainContent)

        // Focus the textarea after setting the content
        if (editInputRef.current) {
          setTimeout(() => {
            const input = editInputRef.current
            if (input) {
              input.focus()
              // Always place cursor at the beginning of the main editable area
              input.setSelectionRange(0, 0)
            }
          }, 0)
        }
      }
    }, [isEditing, messageText, codeBlocks, getEditableContent])

    useEffect(() => {
      if (messageImagePaths.length === 0) {
        setResolvedImages({
          sourceKey: messageImageSourceKey,
          urls: []
        })
        return
      }

      let isCancelled = false

      Promise.all(
        messageImagePaths.map((filePath) => {
          const storagePath = normalizeMessageImageStorageSource(filePath)
          const safeUrl = getSafeRenderableImageUrl(filePath)

          if (storagePath && !getSafeRenderableImageUrl(storagePath)) {
            return getMessageImageFromStorage(storagePath)
              .then(preloadImageUrl)
              .catch(() => safeUrl ?? null)
          }

          if (safeUrl) {
            return Promise.resolve(safeUrl)
          }

          return storagePath
            ? getMessageImageFromStorage(storagePath).then(preloadImageUrl).catch(() => null)
            : Promise.resolve(null)
        })
      ).then((resolvedImagePaths) => {
        // The signed-URL swap only reaches the DOM once the bytes are already
        // cached (preloadImageUrl), so <img src> flips to it without a
        // visible spinner/reload.
        if (isCancelled) return

        setResolvedImages({
          sourceKey: messageImageSourceKey,
          urls: resolvedImagePaths.filter((path): path is string => Boolean(path))
        })
      })

      return () => {
        isCancelled = true
      }
    }, [messageImagePaths, messageImageSourceKey])

    const MODEL_DATA = useMemo(
      () => (allModels ?? []).find((llm) => llm.modelId === message.model) as LLM,
      [allModels, message]
    )

    // State to track note creation locally for immediate UI updates
    const [hasNoteLocal, setHasNoteLocal] = useState(false)

    // Determine if this message has an associated note via annotations or content markers
    const hasNote = useMemo(() => {
      try {
        const contentHasNote = REGEX_NOTE.test(messageText)
        const annotationHasNote = Boolean((message as any)?.annotation?.editableContent?.isActive)
        return contentHasNote || annotationHasNote || hasNoteLocal
      } catch {
        return hasNoteLocal
      }
    }, [messageText, (message as any)?.annotation, hasNoteLocal])

    const [createdSpaceNote, setCreatedSpaceNote] = useState<CreatedSpaceNote | null>(null)
    const [createdSpaceNoteDestination, setCreatedSpaceNoteDestination] =
      useState<SpaceNoteDestination | null>(null)
    const [createdSpaceTask, setCreatedSpaceTask] = useState<CreatedSpaceTask | null>(null)
    const [createdSpaceTaskDestination, setCreatedSpaceTaskDestination] =
      useState<SpaceNoteDestination | null>(null)

    const handleSpaceNoteCreated = useCallback(
      (note: CreatedSpaceNote, destination: SpaceNoteDestination) => {
        setCreatedSpaceNote(note)
        setCreatedSpaceNoteDestination(destination)
        setHasNoteLocal(true)
      },
      []
    )

    const handleSpaceTaskCreated = useCallback(
      (task: CreatedSpaceTask, destination: SpaceNoteDestination) => {
        setCreatedSpaceTask(task)
        setCreatedSpaceTaskDestination(destination)
        setHasNoteLocal(true)
      },
      []
    )

    const fileAccumulator: Record<
      string,
      {
        id: string
        name: string
        count: number
        type: string
        description: string
      }
    > = {}

    const fileSummary = useMemo(
      () =>
        fileItems.reduce((acc, fileItem) => {
          const parentFile = files.find((file) => file.id === fileItem.file_id)
          if (parentFile) {
            if (!acc[parentFile.id]) {
              acc[parentFile.id] = {
                id: parentFile.id,
                name: parentFile.name,
                count: 1,
                type: parentFile.type,
                description: parentFile.description
              }
            } else {
              acc[parentFile.id].count += 1
            }
          }
          return acc
        }, fileAccumulator),
      [fileItems, files]
    )

    const hasReasoningSummary = useMemo(() => {
      try {
        const ann = message.annotation as unknown
        if (Array.isArray(ann)) {
          return ann.some(hasReasoningSummaryText)
        }
        return hasReasoningSummaryText(ann)
      } catch {
        return false
      }
    }, [message.annotation])

    const toolTimeline = useMemo(() => {
      const ann = message.annotation as Record<string, unknown> | null
      const timeline = ann?.tool_timeline
      if (!Array.isArray(timeline)) return null
      return timeline as ToolTimelineEntry[]
    }, [message.annotation])

    // When the rich ToolActivityLog is going to render (any per-tool
    // annotation key present), suppress the sparser ToolTimeline "Complete"
    // list to avoid the double-tool-component duplication.
    const hasToolActivityAnnotation = useMemo(() => {
      const ann = message.annotation as Record<string, unknown> | null
      if (!ann) return false
      return Object.keys(ann).some((key) => isToolActivityKey(key, ann))
    }, [message.annotation])

    const renderSegments = useMemo(() => {
      const ann = message.annotation as Record<string, unknown> | null
      const segments = ann?.render_segments
      if (!Array.isArray(segments)) return null
      return segments as MessageRenderSegment[]
    }, [message.annotation])

    // When reasoning is persisted as inline segments, the segmented render
    // path shows each reasoning run in place. Suppress the top single
    // "Thought for Ns" block so reasoning does not double-render.
    const hasReasoningSegments = useMemo(
      () => Boolean(renderSegments?.some((s) => s.type === 'reasoning')),
      [renderSegments]
    )

    const toolTimelineById = useMemo(() => {
      const map = new Map<string, ToolTimelineEntry>()
      if (!toolTimeline) return map
      for (const entry of toolTimeline) {
        map.set(entry.id, entry)
      }
      return map
    }, [toolTimeline])

    const isBottomAnnotationKey = (key: string) =>
      key.startsWith('signposting__') ||
      key.startsWith('CredentialVault__') ||
      key.startsWith('computerUse__') ||
      key.startsWith('priceComparison__') ||
      key.startsWith('eventDiscovery__') ||
      key.startsWith('smartReminder__') ||
      key.startsWith('liveScores__') ||
      key.startsWith('losiCode__request_connector') ||
      key === 'losiCode__check_coding_readiness'

    const renderAnnotations = (annotation: Annotation, position: 'top' | 'bottom' = 'top') => {
      if (!annotation) {
        return null
      }

      if (Array.isArray(annotation)) {
        // Preserve multiple web search results instead of flattening them.
        // The old reducer overwrote duplicate keys like `webScraper__googleSearch`.
        const merged = annotation.reduce<Record<string, unknown>>((acc, item) => {
          if (!item || typeof item !== 'object') return acc
          for (const [key, value] of Object.entries(item as Record<string, unknown>)) {
            if (key === 'reasoning_summary' || key === 'reasoning_summary_stream') {
              const currentPayload = parseReasoningSummaryPayload(acc[key])
              const nextPayload = parseReasoningSummaryPayload(value)
              if (!nextPayload) {
                acc[key] = value
                continue
              }

              acc[key] = {
                ...currentPayload,
                ...nextPayload,
                text: mergeReasoningSummaryText(currentPayload?.text ?? '', nextPayload.text ?? '')
              }
              continue
            }

            if (key === 'webScraper__googleSearch') {
              const existing = acc[key]
              if (existing === undefined) {
                acc[key] = [value]
              } else if (Array.isArray(existing)) {
                acc[key] = [...existing, value]
              } else {
                acc[key] = [existing, value]
              }
              continue
            }

            if (key === 'losi_steps') {
              acc[key] = mergeLosiSteps(acc[key], value)
              continue
            }

            acc[key] = value
          }
          return acc
        }, {})

        annotation = merged as Annotation
      }

      // Check if we have a search_status annotation indicating search in progress
      // const searchStatus = (annotation as any).search_status;
      // if (searchStatus && searchStatus.status === "searching") {
      //   return (
      //     <div className="flex items-center space-x-2 text-sm text-muted-foreground py-2">
      //       <div className="relative" style={{ display: 'inline-block' }}>
      //         <IconWorld
      //           className="text-muted-foreground"
      //           size={16}
      //           style={{
      //             animation: 'rotate-globe 2s linear infinite',
      //             transformOrigin: 'center'
      //           }}
      //         />
      //         <style jsx>{`
      //           @keyframes rotate-globe {
      //             0% { transform: rotateY(0deg); }
      //             100% { transform: rotateY(360deg); }
      //           }
      //         `}</style>
      //       </div>
      //       <span>{searchStatus.message || "Searching the web..."}</span>
      //     </div>
      //   );
      // }

      const annotationMap: {
        [key: string]: React.FC<{ annotation: Annotation | Annotation2 }>
      } = {
        webScraper__youtubeCaptions: YouTube,
        webScraper__googleSearch: WebSearch,
        selected_html_elements: SelectedHtmlElements,
        taskAutomation__createTask: Task,
        taskAutomation__listTasks: Task,
        taskAutomation__runTask: Task,
        taskAutomation__scheduleTask: Task,
        taskAutomation__editTask: Task,
        taskAutomation__createInstantTask: Task,
        webScraper__getWeather: AnnotationWeather,
        yahooFinance__get_stock_data: YahooFinanceCard,
        currentTime__getCurrentTime: TimeAnnotation,
        signposting__requestLocation: SignpostingLocationRequestAnnotation,
        CredentialVault__requestLogin: CredentialVaultLoginRequestAnnotation,
        CredentialVault__requestSecret: CredentialVaultLoginRequestAnnotation,
        computerUse__requestCredential: CredentialVaultLoginRequestAnnotation,
        losiCode__request_connector: ConnectorRequestAnnotation,
        liveScores__watchGame: LiveScoreAnnotation,
        liveScores__getLiveScores: LiveScoreAnnotation,
        signposting__searchNearbyServices: SignpostingPlacesAnnotation,
        eventDiscovery__searchLocalEvents: EventDiscoveryAnnotation,
        smartReminder__createReminder: SmartReminderAnnotation,
        priceComparison__compareProductPrices: PriceComparisonAnnotation,
        deepResearch: DeepResearchAnnotation,
        platformTools: DeepResearchAnnotation,
        audioTool__processAudio: AudioToolAnnotation,
        slideCreator__createPresentation: AnnotationPresentation,
        slideCreator__addTitleSlide: AnnotationPresentation,
        slideCreator__addContentSlide: AnnotationPresentation,
        slideCreator__addChartSlide: AnnotationPresentation,
        slideCreator__addTableSlide: AnnotationPresentation,
        slideCreator__addImageSlide: AnnotationPresentation,
        slideCreator__exportPresentation: AnnotationPresentation,
        // CRM Tools
        crm__create_lead: CrmCreateLeadAnnotation,
        crm__update_lead: CrmUpdateLeadAnnotation,
        crm__search_leads: CrmSearchLeadsAnnotation,
        crm__get_lead_details: CrmGetLeadDetailsAnnotation,
        crm__get_email_engagement: CrmGetEmailEngagementAnnotation,
        crm__get_lead_insights: CrmGetLeadInsightsAnnotation,
        crm__suggest_follow_up: CrmSuggestFollowUpAnnotation,
        crm__send_email: CrmSendEmailAnnotation,
        crm__send_sms: CrmSendSmsAnnotation,
        crm__schedule_activity: CrmScheduleActivityAnnotation,
        NexusCRMTool__run: ({ annotation }: { annotation: Annotation | Annotation2 }) => (
          <NexusCrmUnifiedAnnotation annotation={annotation} toolKey="NexusCRMTool__run" />
        ),
        // Agent Management Tools
        nexus__list_agents: NexusListAgentsAnnotation,
        nexus__get_agent: NexusGetAgentAnnotation,
        nexus__create_agent: NexusCreateAgentAnnotation,
        nexus__update_agent: NexusUpdateAgentAnnotation,
        nexus__improve_agent: NexusImproveAgentAnnotation,
        GeneratedIntegrationTool__draftIntegration: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <GeneratedIntegrationDraftAnnotation annotation={annotation as Record<string, unknown>} />
        ),
        GeneratedIntegrationTool__approveIntegrationDraft: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <GeneratedIntegrationDraftAnnotation annotation={annotation as Record<string, unknown>} />
        ),
        GeneratedIntegrationTool__updateIntegration: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <GeneratedIntegrationDraftAnnotation annotation={annotation as Record<string, unknown>} />
        ),
        GeneratedIntegrationTool__deleteIntegration: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <GeneratedIntegrationDraftAnnotation annotation={annotation as Record<string, unknown>} />
        ),
        threeDAssetGenerator__generate3DAsset: ThreeDAssetAnnotation,
        losi_steps: ({ annotation }: { annotation: Annotation | Annotation2 }) => {
          const steps = normalizeLosiSteps((annotation as Record<string, unknown>).losi_steps)
          if (steps.length === 0) return null
          return <LosiStepsChainOfThought steps={steps} />
        },
        sidecar_run: ({ annotation }: { annotation: Annotation | Annotation2 }) => (
          <SidecarRunAnnotation annotation={annotation} />
        ),
        losiCode__start_coding_run: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => {
          const record = annotation as Record<string, unknown>
          const raw = record['losiCode__start_coding_run'] as
            | Record<string, unknown>
            | undefined
          const result =
            raw && typeof raw === 'object'
              ? (('result' in raw ? raw.result : raw) as {
                  needsConnector?: boolean
                  missingConnectors?: string[]
                })
              : null

          if (result?.needsConnector || (result?.missingConnectors?.length ?? 0) > 0) {
            return <ConnectorRequestAnnotation annotation={annotation} />
          }

          return (
            <LosiCodeAnnotation
              annotation={annotation}
              toolKey="losiCode__start_coding_run"
            />
          )
        },
        losiCode__get_coding_run_status: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <LosiCodeAnnotation
            annotation={annotation}
            toolKey="losiCode__get_coding_run_status"
          />
        ),
        losiCode__run_verification: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <LosiCodeAnnotation
            annotation={annotation}
            toolKey="losiCode__run_verification"
          />
        ),
        losiCode__ensure_coding_task: ({
          annotation
        }: {
          annotation: Annotation | Annotation2
        }) => (
          <LosiCodeAnnotation
            annotation={annotation}
            toolKey="losiCode__ensure_coding_task"
          />
        ),
        losiCode__check_coding_readiness: ConnectorRequestAnnotation
      }

      if (showResponseTime) {
        // expose tool call timings when requested
        annotationMap.toolCalls = ToolCalls
      }

      const annotationResponseTimeLabelMap: {
        [key: string]: string
      } = {
        webScraper__youtubeCaptions: 'YouTube',
        webScraper__googleSearch: 'Web search',
        webScraper__FetchDataFromUrl: 'Fetch URL',
        webScraper__getWeather: 'Weather',
        webScraper__renderManimAnimation: 'Animation',
        webScraper__manimAnimation: 'Animation',
        taskAutomation__createTask: 'Task creation',
        taskAutomation__listTasks: 'Task list',
        taskAutomation__runTask: 'Task execution',
        taskAutomation__scheduleTask: 'Task scheduling',
        yahooFinance__get_stock_data: 'Finance',
        reasoning_summary: 'Reasoning',
        reasoning_summary_stream: 'Reasoning',
        currentTime__getCurrentTime: 'Time',
        signposting__searchNearbyServices: 'Nearby places',
        signposting__requestLocation: 'Location request',
        CredentialVault__requestLogin: 'Private vault login',
        CredentialVault__requestSecret: 'Private vault',
        computerUse__requestCredential: 'Private vault login',
        losiCode__request_connector: 'Connector request',
        losiCode__check_coding_readiness: 'Coding readiness',
        liveScores__watchGame: 'Live score',
        liveScores__getLiveScores: 'Live scores',
        eventDiscovery__searchLocalEvents: 'Event discovery',
        smartReminder__createReminder: 'Reminder',
        priceComparison__compareProductPrices: 'Price comparison',
        deepResearch: 'Deep research',
        platformTools: 'Platform actions',
        audioTool__processAudio: 'Audio',
        threeDAssetGenerator__generate3DAsset: '3D asset',
        MemoryTool__storeMemoryToRemember: 'Memory',
        MemoryTool__listMemories: 'Memories',
        MemoryTool__updateMemory: 'Memory',
        MemoryTool__deleteMemory: 'Memory',
        ChatSearch__searchChats: 'Chat search',
        BookmarkSearch__searchBookmarks: 'Bookmarks',
        Sidecar__delegate: 'Sidecar',
        sidecar_run: 'Sidecar',
        losiCode__start_coding_run: 'Losi Code',
        losiCode__get_coding_run_status: 'Losi Code',
        losiCode__run_verification: 'Losi Code',
        losiCode__ensure_coding_task: 'Losi Code',
        StarredMessagesSearch__searchStarredMessages: 'Starred messages',
        ChatManagementTool__getChats: 'Chats',
        ChatManagementTool__getChatMessages: 'Chat messages',
        ChatManagementTool__pinChat: 'Pin chat',
        ChatManagementTool__starMessage: 'Star message',
        ConnectedServices__listConnectedExtensions: 'Connected integrations',
        ConnectedServices__listActionsForExtension: 'Actions',
        ConnectedServices__executeExtensionAction: 'Execute action',
        ConnectedServices__checkToolkitConnection: 'Integration connection',
        NexusCRMTool__run: 'Nexus Contacts',
        GeneratedIntegrationTool__draftIntegration: 'Generated integration',
        GeneratedIntegrationTool__approveIntegrationDraft: 'Created integration',
        GeneratedIntegrationTool__updateIntegration: 'Updated integration',
        GeneratedIntegrationTool__deleteIntegration: 'Deleted integration',
        ToolManagementTool__enableTool: 'Enable Tool',
        ToolManagementTool__disableTool: 'Disable Tool',
        ToolManagementTool__listAvailableTools: 'Available Tools',
        unifiedImage__imageGenerationViaunifiedImage: 'Image',
        unifiedImage__imageEditViaunifiedImage: 'Image edit',
        VirtualFilesystem__losi__fs_list_files: 'Virtual files',
        VirtualFilesystem__losi__fs_read_file: 'Virtual files',
        VirtualFilesystem__losi__fs_write_file: 'Virtual files',
        VirtualFilesystem__losi__fs_patch_file: 'Virtual files'
      }

      const annRecord = annotation as Record<string, unknown>
      const hasToolActivity = Object.keys(annotation).some((key) =>
        isToolActivityKey(key, annRecord)
      )
      const hasVirtualFileCreated =
        'VirtualFilesystem__losi__fs_write_file' in annRecord ||
        'VirtualFilesystem__losi__fs_patch_file' in annRecord
      const shouldSuppressSidecarReportFile =
        hasVirtualFileCreated && isSidecarReportVirtualFileAnnotation(annRecord)
      // When the same write/patch tool is already painted inline via
      // render_segments → WorkedFor/ToolTimeline, skip the top duplicate card.
      const vfsToolInSegments = Boolean(
        renderSegments?.some(
          (segment) =>
            segment.type === 'tool' &&
            typeof segment.toolId === 'string' &&
            VIRTUAL_FILE_CREATED_KEYS.some(
              (key) =>
                segment.toolId === key ||
                segment.toolId.includes(key) ||
                segment.toolId.replace(/^platform-/, '').replace(/-\d+$/, '') ===
                  key
            )
        )
      )
      const shouldShowVirtualFileCreated =
        hasVirtualFileCreated &&
        !shouldSuppressSidecarReportFile &&
        !vfsToolInSegments
      const hasPlanCreated =
        annRecord.show_plan != null &&
        typeof annRecord.show_plan === 'object' &&
        'planId' in (annRecord.show_plan as object)
      const reasoningSummary = getReasoningSummaryRenderData(annotation, isGenerating)
      const freePlanToolLimitCtaPayload = getFreePlanToolLimitCtaPayload(annotation)
      const annotationKeys = Object.keys(annotation)
        .filter((key) => {
          if (isToolActivityKey(key, annRecord)) {
            return false
          }

          if (shouldSkipDuplicateRichAnnotation(key, annRecord)) {
            return false
          }

          // When tools render inline via render_segments → ToolTimeline, skip
          // the bottom LiveScoreAnnotation copy so cards don't double up.
          if (
            position === 'bottom' &&
            renderSegments &&
            key.startsWith('liveScores__')
          ) {
            return false
          }

          if (key === 'reasoning_summary' || key === 'reasoning_summary_stream') {
            return false
          }

          if (key === 'free_plan_tool_limit') {
            return false
          }

          if (key === 'model_switch') {
            return false
          }

          if (
            shouldSuppressSidecarReportFile &&
            VIRTUAL_FILE_CREATED_KEYS.includes(key)
          ) {
            return false
          }

          // Signposting annotations render at bottom, others at top
          if (position === 'top' && isBottomAnnotationKey(key)) {
            return false
          }
          if (position === 'bottom' && !isBottomAnnotationKey(key)) {
            return false
          }

          return true
        })
        .sort((first, second) => {
          const firstDesktop = isDesktopToolAnnotationKey(first)
          const secondDesktop = isDesktopToolAnnotationKey(second)

          if (firstDesktop !== secondDesktop) {
            return firstDesktop ? -1 : 1
          }

          const firstToolCalls = first === 'toolCalls'
          const secondToolCalls = second === 'toolCalls'
          if (firstToolCalls !== secondToolCalls) {
            return firstToolCalls ? 1 : -1
          }

          return 0
        })

      const hasVisibleAnnotationContent =
        (position === 'top' && (
          hasPlanCreated ||
          shouldShowVirtualFileCreated ||
          hasToolActivity ||
          Boolean(freePlanToolLimitCtaPayload) ||
          Boolean(reasoningSummary)
        )) ||
        annotationKeys.length > 0

      if (!hasVisibleAnnotationContent) {
        return null
      }

      return (
        <div className={MESSAGE_ANNOTATION_STACK_CLASS}>
          {position === 'top' && hasPlanCreated && (
            <PlanCreatedAnnotation annotation={annRecord} chatId={message.chat_id} />
          )}
          {position === 'top' && shouldShowVirtualFileCreated && (
            <VirtualFileCreatedAnnotation annotation={annRecord} />
          )}
          {position === 'top' && hasToolActivity && !renderSegments && (
            <ToolActivityLog annotation={annRecord} />
          )}
          {position === 'top' && freePlanToolLimitCtaPayload && (
            <GetPremiumMarkdownButton
              className="not-prose"
              payloadText={freePlanToolLimitCtaPayload}
            />
          )}
          {position === 'top' &&
            reasoningSummary &&
            !hasReasoningSegments && (
              <ReasoningSummaryBlock
                key="reasoning_summary"
                summary={reasoningSummary}
                isGenerating={isGenerating}
              />
            )}
          {annotationKeys.map((key) => {
            const isNexusCrmKey = key.startsWith('crm__') || key.startsWith('nexus__')
            const isDesktopToolKey = isDesktopToolAnnotationKey(key)
            const isDeepResearchKey = key.startsWith('deepResearch') || key === 'platformTools'
            const AnnotationComponent =
              annotationMap[key] ??
              (isDeepResearchKey ? DeepResearchAnnotation : undefined) ??
              (isNexusCrmKey ? null : undefined)
            const rawToolEntry =
              annRecord[key] && typeof annRecord[key] === 'object'
                ? (annRecord[key] as {
                    result?: unknown
                    request?: unknown
                    requestTime?: number
                    responseTime?: number
                    status?: 'running' | 'progress' | 'complete' | 'error'
                  })
                : null
            const richToolResult =
              !AnnotationComponent &&
              !isNexusCrmKey &&
              !isDesktopToolKey &&
              !key.startsWith('losiCode__') &&
              rawToolEntry
                ? renderToolResultForEntry({
                    key,
                    name: key,
                    durationMs: rawToolEntry.requestTime ?? rawToolEntry.responseTime ?? 0,
                    result: rawToolEntry.result,
                    request: rawToolEntry.request,
                    status: rawToolEntry.status
                  })
                : null

            if (!AnnotationComponent && !isNexusCrmKey && !isDesktopToolKey && !richToolResult)
              return null
            const responseTimeLabel =
              annotationResponseTimeLabelMap[key] ||
              getDesktopToolAnnotationResponseTimeLabel(key)
            const rawResponseTime =
              responseTimeLabel && rawToolEntry
                ? rawToolEntry.responseTime
                : undefined
            const responseTime = typeof rawResponseTime === 'number' ? rawResponseTime : 0

            return (
              <div key={key} className={MESSAGE_ANNOTATION_ITEM_CLASS}>
                {isDesktopToolKey ? (
                  <DesktopToolAnnotation annotation={annotation} toolKey={key} />
                ) : AnnotationComponent ? (
                  <AnnotationComponent annotation={annotation} />
                ) : richToolResult ? (
                  richToolResult
                ) : (
                  <GenericNexusCrmAnnotation annotation={annotation} toolKey={key} />
                )}
                {showResponseTime && responseTimeLabel && responseTime > 0 && (
                  <ResponseTime
                    icon={<IconApi stroke={1.5} size={18} />}
                    label={responseTimeLabel}
                    // @ts-ignore
                    value={responseTime}
                  />
                )}
              </div>
            )
          })}
        </div>
      )
    }

    let assistant =
      message.role === 'assistant' && message.assistant_id
        ? (assistants ?? []).find((assistant) => assistant.id === message.assistant_id)
        : null

    const participantMeta = (() => {
      const ann = message.annotation as Record<string, unknown> | null
      if (!ann || ann.participant !== true) return null
      const name =
        typeof ann.assistant_name === 'string' && ann.assistant_name.trim()
          ? ann.assistant_name.trim()
          : null
      const invocationId =
        typeof ann.invocation_id === 'string' ? ann.invocation_id : null
      return { name, invocationId }
    })()

    if (!assistant && participantMeta?.name && message.assistant_id) {
      // Participant reply-as: assistant may not be the host selectedAssistant.
      assistant = {
        id: message.assistant_id,
        name: participantMeta.name,
        description: '',
        image_path: ''
      } as Tables<'assistants'>
    }

    if (!assistant && selectedAssistant) {
      assistant = selectedAssistant
    }
    const delay = message.role === 'assistant' ? 0.3 : 0

    const messageAnimationKey =
      message.chat_id && message.sequence_number != null
        ? `${message.chat_id}:${message.sequence_number}:${message.role}`
        : `${message.id}:${message.role}`
    const isOptimisticMessage = !message.created_at
    const shouldAnimateEntry =
      isOptimisticMessage && !animatedOptimisticMessageKeys.has(messageAnimationKey)

    useEffect(() => {
      if (shouldAnimateEntry) {
        animatedOptimisticMessageKeys.add(messageAnimationKey)
      }
    }, [messageAnimationKey, shouldAnimateEntry])
    const initialAnimation = shouldAnimateEntry ? 'hidden' : false

    // Stable variants so streaming end doesn't change transition and cause flash
    const defaultVariants = useMemo(
      () => ({
        hidden: {
          opacity: 0,
          y: message.role === 'user' ? 14 : 8,
          filter: 'blur(4px)'
        },
        visible: {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          transition: {
            duration: 0.24,
            ease: [0.22, 1, 0.36, 1],
            delay
          }
        }
      }),
      [delay, message.role]
    )

    const isEmptyLoadingAssistantTurn =
      shouldShowAssistantGenerationState && messageText.trim().length === 0

    const [loaderPose, setLoaderPose] = useState<ChatLosiPose | null>(null)
    const handleLoaderPoseChange = useCallback((pose: ChatLosiPose | null) => {
      setLoaderPose(pose)
    }, [])

    const messageLosiPose = useMemo(() => {
      if (loaderPose) return loaderPose
      return resolveChatLosiPose({
        mode: 'message',
        isReasoning: currentSection === 'thinking',
        isGenerating: shouldShowAssistantGenerationState
      })
    }, [loaderPose, currentSection, shouldShowAssistantGenerationState])

    const crewChatRosterForMessage = useMemo((): CrewChatRoster | null => {
      return message.chat_id ? getCrewChatRosterForChat(message.chat_id) : null
    }, [message.chat_id])

    const crewSpeakerMateId = useMemo(() => {
      if (message.role !== 'assistant') return null
      const fromText = parseCrewChatActiveSpeakerMateId(
        messageText,
        crewChatRosterForMessage
      )
      if (fromText) return fromText
      if (!crewChatRosterForMessage || !message.chat_id) return null
      return getCrewMemberForChat(message.chat_id)
    }, [
      crewChatRosterForMessage,
      message.chat_id,
      message.role,
      messageText
    ])

    const crewSpeakerShape = useMemo(() => {
      if (!crewSpeakerMateId) return undefined
      return mateIdToFeedShape(crewSpeakerMateId) ?? undefined
    }, [crewSpeakerMateId])

    const crewSpeakerLabel = useMemo(() => {
      if (!crewSpeakerMateId) return null
      const seat = crewChatRosterForMessage?.seats.find(
        s => s.mateId === crewSpeakerMateId
      )
      return (
        seat?.displayName ?? MATE_BY_ID.get(crewSpeakerMateId)?.label ?? null
      )
    }, [crewChatRosterForMessage, crewSpeakerMateId])

    // Resolve the mate's visual identity (name, color accent, avatar) from the
    // bare mateId at render time. Fail-closed: a null/unknown mateId yields a
    // null identity and the standard Losi assistant bubble renders unchanged.
    const crewMateIdentity = useMemo<CrewMateRenderIdentity | null>(() => {
      if (message.role !== 'assistant') return null
      return getMateRenderIdentity(crewSpeakerMateId)
    }, [crewSpeakerMateId, message.role])

    // Accent only applies when identity resolves AND the user is actually in a
    // Crew Chat thread (roster present) — never recolor a plain Losi bubble.
    const crewAccentColor =
      crewChatRosterForMessage && crewMateIdentity
        ? crewMateIdentity.color
        : null

    return (
      <motion.div
        initial={initialAnimation}
        id={`message-${message.id}`}
        animate="visible"
        ref={ref}
        variants={defaultVariants}
        className={cn(
          'group flex w-full scroll-mt-28 justify-center',
          className,
          // message.role === "user" ? "" : "bg-secondary",
          'role-' + message.role,
          isLast ? 'is-last' : '',
          isSelectionMode && 'hover:bg-accent/5',
          isSelected && 'bg-accent/10'
        )}
        onKeyDown={handleKeyDown}
      >
        <div className="relative flex w-full min-w-0 max-w-full flex-col overflow-x-clip p-4">
          {/* MessageTimelineIndicator removed per request */}
          <motion.div
            layout="position"
            transition={{ type: 'spring', stiffness: 420, damping: 36, mass: 0.7 }}
            className={cn(
              'space-y-3',
              // Own user messages hug the right (avatar + actions grouped there).
              // Left inset keeps long multiline user text from lining up under
              // the Losi avatar/thought row above — that made user replies look
              // like a continuation of the assistant turn.
              isOwnMessage &&
                !isSidekick &&
                // Logical inset + shrink-to-content prose so short bubbles ("yo")
                // hug the end edge instead of stretching full-width left text.
                'flex flex-col items-end ps-12 sm:ps-16 md:ps-24 [&_.prose]:w-fit [&_.prose]:max-w-full',
              // Group teammates share the start column with Losi (content + avatar).
              isOtherUserMessage &&
                !isSidekick &&
                'flex flex-col items-start pe-12 sm:pe-16 md:pe-24'
            )}
          >
            {message.role === 'system' ? (
              <div className="flex items-center space-x-4">
                <IconBulb
                  className="border-primary bg-primary text-secondary rounded border p-1"
                  size={ICON_SIZE}
                />

                <div className="text-lg font-semibold">Prompt</div>
              </div>
            ) : null}
            {createdSpaceTask && createdSpaceTaskDestination ? (
              <div
                className={cn(
                  'flex w-full',
                  isOwnMessage && !isSidekick ? 'justify-end' : 'justify-start'
                )}
              >
                <SpaceCreatedItemBlock
                  className="max-w-sm"
                  createdItem={{
                    type: 'task',
                    item: createdSpaceTask
                  }}
                  destination={createdSpaceTaskDestination}
                />
              </div>
            ) : null}
            {createdSpaceNote && createdSpaceNoteDestination ? (
              <div
                className={cn(
                  'flex w-full',
                  isOwnMessage && !isSidekick ? 'justify-end' : 'justify-start'
                )}
              >
                <SpaceCreatedItemBlock
                  className="max-w-sm"
                  createdItem={{
                    type: 'note',
                    item: createdSpaceNote
                  }}
                  destination={createdSpaceNoteDestination}
                />
              </div>
            ) : null}
            {participantMeta ? (
              <div
                className="bg-secondary/40 text-muted-foreground mb-1 inline-flex max-w-full items-center gap-2 rounded-md px-2.5 py-1 text-xs"
                data-testid="assistant-participant-chip"
              >
                <span className="text-foreground font-medium truncate">
                  {participantMeta.name || assistant?.name || 'Assistant'}
                </span>
                <span className="opacity-70">replied as participant</span>
              </div>
            ) : null}
            {message.role === 'assistant' && message.annotation ? (
              <ContentEngineApprovalCard
                annotation={message.annotation}
                disabled={shouldShowAssistantGenerationState}
                onConfirmInteractive={onConfirmInteractive}
              />
            ) : null}
            {message.role === 'assistant' && message.annotation ? (
              <PlanApprovalCard
                annotation={
                  message.annotation
                    ? (message.annotation as
                        | Record<string, unknown>
                        | unknown[])
                    : null
                }
                disabled={shouldShowAssistantGenerationState}
                onApprove={async content => {
                  if (!message.chat_id) return
                  const response = await fetch(`/api/chat/plan/${message.chat_id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content, status: 'active' })
                  })
                  if (!response.ok) {
                    toast.error(t('Failed to approve plan'))
                    return
                  }
                  toast.success(t('Plan approved'))
                  const data = (await response.json().catch(() => null)) as {
                    plan?: { id?: string }
                    planId?: string
                    id?: string
                  } | null
                  const annotationPlanId = (() => {
                    const ann = message.annotation
                    if (!ann || typeof ann !== 'object') return null
                    const entries = Array.isArray(ann) ? ann : [ann]
                    for (const entry of entries) {
                      if (!entry || typeof entry !== 'object') continue
                      const raw =
                        (entry as Record<string, unknown>).propose_plan ||
                        (entry as Record<string, unknown>).show_plan
                      if (
                        raw &&
                        typeof raw === 'object' &&
                        typeof (raw as { planId?: unknown }).planId === 'string'
                      ) {
                        return (raw as { planId: string }).planId
                      }
                    }
                    return null
                  })()
                  const planId =
                    (typeof data?.plan?.id === 'string' && data.plan.id) ||
                    (typeof data?.planId === 'string' && data.planId) ||
                    (typeof data?.id === 'string' && data.id) ||
                    annotationPlanId
                  if (!planId) return
                  window.dispatchEvent(
                    new CustomEvent('openPlanInCanvas', {
                      detail: { planId, content, status: 'active' }
                    })
                  )
                }}
                onReject={async () => {
                  if (!message.chat_id) return
                  const response = await fetch(`/api/chat/plan/${message.chat_id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: 'rejected' })
                  })
                  if (!response.ok) {
                    toast.error(t('Failed to reject plan'))
                    return
                  }
                  toast.success(t('Plan rejected'))
                }}
              />
            ) : null}
            {/* Thinking/reasoning renders above tool activity */}
            {(() => {
              const showThinkingAbove = messageContentParts.thinkingContent && !hasReasoningSummary && shouldShowAssistantGenerationState
              return showThinkingAbove ? (
                <ContinuousReasoningBlock
                  content={messageContentParts.thinkingContent!}
                  isStreaming={shouldShowAssistantGenerationState}
                />
              ) : null
            })()}
            {message.annotation && (
              <div className="w-full">{renderAnnotations(message.annotation as Annotation, 'top')}</div>
            )}
            {chatContextCompaction ? (
              <div className="border-border/70 bg-muted/30 text-muted-foreground flex w-full max-w-2xl items-start gap-3 rounded-lg border px-3 py-2 text-sm">
                <IconArchive className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <div className="min-w-0 space-y-1">
                  <div className="text-foreground font-medium">{compactionStatusLabel}</div>
                  <div className="text-xs leading-relaxed">
                    {chatContextCompaction.droppedMessageCount
                      ? `${chatContextCompaction.droppedMessageCount} older messages were summarized so this turn can continue.`
                      : 'Older messages were summarized so this turn can continue.'}
                  </div>
                </div>
              </div>
            ) : null}
            {isOtherUserMessage && userSenderName ? (
              <div className="text-muted-foreground text-xs font-medium">
                {userSenderName}
              </div>
            ) : null}
            {(() => {
              return (
                <>
                  {messageContentParts.thinkingContent && !hasReasoningSummary && !shouldShowAssistantGenerationState && (
                    <ContinuousReasoningBlock
                      content={messageContentParts.thinkingContent}
                      isStreaming={false}
                    />
                  )}
                  {!firstTokenReceived && shouldShowAssistantGenerationState ? (
                    hasReasoningSummary ? (
                      <LoadingMessage
                        isGenerating={isGenerating}
                        currentSection={currentSection}
                        showToolsOnly={true}
                        chatId={message.chat_id}
                        generationStartedAt={generationStartedAt}
                        onPoseChange={handleLoaderPoseChange}
                      />
                    ) : (
                      <LoadingMessage
                        isGenerating={isGenerating}
                        currentSection={currentSection}
                        showToolsOnly={false}
                        chatId={message.chat_id}
                        generationStartedAt={generationStartedAt}
                        onPoseChange={handleLoaderPoseChange}
                      />
                    )
                  ) : isEditing ? (
                    <div className="flex w-full flex-col space-y-4">
                      {extractedComponents && (
                        <div className="border-secondary/30 bg-secondary/10 rounded-md border p-3">
                          <MessageMarkdown
                            isGenerating={false}
                            codeBlocks={codeBlocks}
                            content={extractedComponents}
                            experimental_code_editor={isExperimentalCodeEditor}
                            messageId={message.id}
                            onSelectMessage={onSelectMessage}
                            message={message}
                            highlights={highlights}
                            onCreateHighlight={onCreateHighlight}
                            highlightQuoteText={highlightQuoteText}
                            onInteractiveSubmit={onConfirmInteractive}
                          />
                        </div>
                      )}
                      <TextareaAutosize
                        textareaRef={editInputRef}
                        className="text-md bg-background border-border w-full rounded-lg border px-3 py-2"
                        value={mainContent}
                        onValueChange={setMainContent}
                        maxRows={20}
                        placeholder="Edit your message here..."
                      />
                    </div>
                  ) : needsTruncation() && !isExpanded ? (
                    // Truncated message view
                    <div className="relative">
                      {/* Always extract and show quote first, if present */}
                      {quoteInfo && (
                        <MessageMarkdown
                          isGenerating={false}
                          codeBlocks={codeBlocks}
                          content={extractedComponents}
                          onSelectCodeBlock={onSelectCodeBlock}
                          experimental_code_editor={isExperimentalCodeEditor}
                          messageId={message.id}
                          onSelectMessage={onSelectMessage}
                          message={message}
                          highlights={highlights}
                          onCreateHighlight={onCreateHighlight}
                          highlightQuoteText={highlightQuoteText}
                          onInteractiveSubmit={onConfirmInteractive}
                        />
                      )}

                      <motion.div
                        initial={{ height: 'auto', opacity: 1 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="max-h-[50vh] overflow-hidden"
                      >
                        <MessageMarkdown
                          animateOnOpen={animateNudgeFirstRead}
                          isGenerating={shouldRenderStreamingMarkdown}
                          codeBlocks={codeBlocks}
                          content={quoteInfo ? truncatedContentForDisplay : displayAssistantContent}
                          onSelectCodeBlock={onSelectCodeBlock}
                          experimental_code_editor={isExperimentalCodeEditor}
                          messageId={message.id}
                          onSelectMessage={onSelectMessage}
                          message={message}
                          highlights={highlights}
                          onCreateHighlight={onCreateHighlight}
                          highlightQuoteText={highlightQuoteText}
                          onInteractiveSubmit={onConfirmInteractive}
                        />
                        <div
                          className="from-background pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent"
                          aria-hidden="true"
                        />
                      </motion.div>
                      <div className="mt-2 flex justify-center">
                        <button
                          onClick={() => setIsExpanded(true)}
                          className="bg-secondary/50 text-foreground/80 hover:bg-secondary/90 flex items-center gap-1 rounded-full px-4 py-1 text-sm transition-colors"
                        >
                          <IconCaretDownFilled size={14} />
                          Show more
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Full message view
                    <div
                      className={cn(
                        // Own user messages hug the right. Without this, an
                        // attached block card (e.g. the trend card) stretches
                        // the body to full width and the small body text
                        // ("More on this") ends up left-aligned. items-end makes
                        // both the card and the text shrink-to-content and align
                        // right like a normal user bubble. max-w-full keeps
                        // pasted tables/code from blowing past the chat column.
                        // w-full + parent pl-* insets long multiline user text
                        // away from the Losi column on the left.
                        isOwnMessage &&
                          !isSidekick &&
                          'ms-auto flex w-full max-w-full flex-col items-end [&_.prose]:w-fit [&_.prose]:max-w-full'
                      )}
                    >
                      {message.role === 'user' && voiceMemo ? (
                        <VoiceMemoMessage
                          voiceMemo={voiceMemo}
                          onTranscriptSave={handleVoiceMemoTranscriptSave}
                        />
                      ) : null}
                      {message.role === 'assistant' &&
                      renderSegments &&
                      renderSegments.some(
                        (s) => s.type === 'reasoning' || s.type === 'tool'
                      ) ? (
                        <div className="space-y-3">
                          {(() => {
                            // Collapse consecutive tools+thoughts between answer
                            // text into "Worked for n seconds" groups. Empty text
                            // slices do not break a group; real prose does.
                            const segmentAnnotationRecord =
                              (message.annotation as Record<string, unknown> | null) ?? null
                            const blocks = buildWorkedForBlocks({
                              segments: renderSegments,
                              contentLength: displayAssistantContent.length,
                              content: displayAssistantContent,
                              toolTimelineById,
                              isGenerating,
                              resolveToolFallback: (toolId) => {
                                const toolKey = resolveToolAnnotationKeyForSegment(
                                  segmentAnnotationRecord,
                                  toolId
                                )
                                if (!segmentAnnotationRecord || !toolKey) {
                                  return null
                                }
                                // liveScores is a rich card (isToolActivityKey=false),
                                // but still needs a WorkedFor fallback when
                                // tool_timeline is empty after finalize/reload.
                                const isLiveScores = toolKey.startsWith('liveScores__')
                                if (
                                  !isLiveScores &&
                                  !isToolActivityKey(toolKey, segmentAnnotationRecord)
                                ) {
                                  return null
                                }
                                if (!(toolKey in segmentAnnotationRecord)) {
                                  return null
                                }
                                return {
                                  activityAnnotation: {
                                    [toolKey]: segmentAnnotationRecord[toolKey]
                                  }
                                }
                              }
                            })

                            return blocks.map((block) => {
                              if (block.type === 'work') {
                                return (
                                  <WorkedForGroup
                                    key={block.id}
                                    items={block.items}
                                    isGenerating={isGenerating}
                                  />
                                )
                              }

                              return (
                                <MessageMarkdown
                                  key={block.id}
                                  animateOnOpen={animateNudgeFirstRead}
                                  isGenerating={shouldRenderStreamingMarkdown}
                                  codeBlocks={codeBlocks}
                                  content={block.content}
                                  onSelectCodeBlock={onSelectCodeBlock}
                                  experimental_code_editor={isExperimentalCodeEditor}
                                  messageId={message.id}
                                  onSelectMessage={onSelectMessage}
                                  message={message}
                                  highlights={highlights}
                                  onCreateHighlight={onCreateHighlight}
                                  highlightQuoteText={highlightQuoteText}
                                  onInteractiveSubmit={onConfirmInteractive}
                                />
                              )
                            })
                          })()}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {message.role === 'assistant' &&
                            !hasToolActivityAnnotation &&
                            toolTimeline &&
                            toolTimeline.length > 0 &&
                            (() => {
                              // Avoid double-rendering a tool that is still in
                              // flight: while generating, the live LoadingMessage
                              // below already shows the running/progress state for
                              // the active tool. So the fallback timeline card
                              // here only shows entries that have already
                              // completed or errored. Once generation ends, show
                              // the full timeline.
                              const visibleFallbackTimeline = isGenerating
                                ? toolTimeline.filter(
                                    (entry) =>
                                      entry.status === 'complete' || entry.status === 'error'
                                  )
                                : toolTimeline
                              if (visibleFallbackTimeline.length === 0) return null
                              return (
                                <ToolTimeline entries={visibleFallbackTimeline} className="mb-0" />
                              )
                            })()}
                          <MessageMarkdown
                            animateOnOpen={animateNudgeFirstRead}
                            isGenerating={shouldRenderStreamingMarkdown}
                            codeBlocks={codeBlocks}
                            content={normalizedFallbackContent}
                            onSelectCodeBlock={onSelectCodeBlock}
                            experimental_code_editor={isExperimentalCodeEditor}
                            messageId={message.id}
                            onSelectMessage={onSelectMessage}
                            message={message}
                            highlights={highlights}
                            onCreateHighlight={onCreateHighlight}
                            highlightQuoteText={highlightQuoteText}
                            onInteractiveSubmit={onConfirmInteractive}
                          />
                        </div>
                      )}
                      {showInteractiveSuggestions && interactiveParse && onConfirmInteractive && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                          className="mt-3 flex flex-wrap gap-2"
                        >
                          {interactiveParse.suggestions.map(
                            (suggestion: InteractiveSuggestion, idx: number) => {
                              if (suggestion.type === 'form') {
                                return (
                                  <FormComponent
                                    key={idx}
                                    suggestion={suggestion}
                                    accent="hsl(var(--primary))"
                                    onConfirm={onConfirmInteractive}
                                    disabled={isGenerating}
                                  />
                                )
                              }
                              if (suggestion.type === 'schedule_calendar') {
                                return (
                                  <ScheduleCalendarComponent
                                    key={idx}
                                    suggestion={suggestion}
                                    accent="hsl(var(--primary))"
                                    onConfirm={onConfirmInteractive}
                                    disabled={isGenerating}
                                  />
                                )
                              }
                              if (suggestion.type === 'schedule_time') {
                                return (
                                  <ScheduleTimeComponent
                                    key={idx}
                                    suggestion={suggestion}
                                    accent="hsl(var(--primary))"
                                    onConfirm={onConfirmInteractive}
                                    disabled={isGenerating}
                                  />
                                )
                              }
                              if (suggestion.type === 'options') {
                                return (
                                  <ChatOptionsConfirm
                                    key={idx}
                                    suggestion={suggestion}
                                    onConfirm={onConfirmInteractive}
                                    disabled={isGenerating}
                                  />
                                )
                              }
                              return null
                            }
                          )}
                        </motion.div>
                      )}
                      {message.role === 'assistant' &&
                      isLast &&
                      !isGenerating &&
                      !shouldShowAssistantGenerationState &&
                      inlineQuestionnaire &&
                      onConfirmInteractive ? (
                        <div className="w-full">
                          <UserQuestionnaire
                            questionnaire={inlineQuestionnaire}
                            hidePrompt
                            variant="inline"
                            onSend={onConfirmInteractive}
                          />
                        </div>
                      ) : null}
                      {message.role === 'assistant' &&
                      isLast &&
                      !isGenerating &&
                      !shouldShowAssistantGenerationState &&
                      !inlineQuestionnaire &&
                      inlineSuggestions.length > 0 &&
                      onConfirmInteractive ? (
                        <SuggestedResponses
                          suggestions={inlineSuggestions}
                          onSend={onConfirmInteractive}
                        />
                      ) : null}
                      {needsTruncation() && isExpanded && (
                        <div className="mt-2 flex justify-center">
                          <button
                            onClick={() => setIsExpanded(false)}
                            className="bg-secondary/50 text-foreground/80 hover:bg-secondary/90 flex items-center gap-1 rounded-full px-4 py-1 text-sm transition-colors"
                          >
                            <IconCaretUpFilled size={14} />
                            Show less
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show tool loading states after streaming starts.
                      When render_segments exist, the RUNNING tool already
                      renders inline at its execution point (its segment's
                      ToolTimeline entry shows the running/progress state), so
                      the detached bottom loader would duplicate it — only show
                      this for the legacy no-segment path. */}
                  {firstTokenReceived &&
                    shouldShowAssistantGenerationState &&
                    !renderSegments && (
                    <div className="mt-3">
                      <LoadingMessage
                        isGenerating={isGenerating}
                        currentSection={currentSection}
                        showToolsOnly={true}
                        chatId={message.chat_id}
                        generationStartedAt={generationStartedAt}
                        onPoseChange={handleLoaderPoseChange}
                      />
                    </div>
                  )}
                </>
              )
            })()}

            {/* Signposting annotations render below message content */}
            {message.annotation && (
              <div className="w-full">{renderAnnotations(message.annotation as Annotation, 'bottom')}</div>
            )}

            {/* Left column (Losi + group teammates): avatar left, actions right.
                Own user: actions then avatar on the right. Do NOT use
                flex-row-reverse for teammates — that packs the row to the right.
                FeatureDiscovery must NOT live in this row — a w-full sibling
                crushes MessageActions to ~0 width on mobile so icons stack
                vertically beside the avatar. */}
            <div
              className={cn(
                'text-muted-foreground flex w-full min-w-0 items-center gap-2',
                isSidekick && 'pt-2'
              )}
            >
              {message.role === 'assistant' ? (
                <div className="flex shrink-0 items-center">
                  {assistant ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <AssistantIcon
                        size={ICON_SIZE + 5}
                        className={`h-[${ICON_SIZE}px] w-[${ICON_SIZE}px]`}
                        assistant={assistant}
                        excludeSpecificLogos={true}
                      />
                      {(participantMeta?.name ||
                        (message.assistant_id &&
                          selectedAssistant?.id !== message.assistant_id)) && (
                        <span className="text-foreground/80 max-w-[4.5rem] truncate text-[10px] font-medium leading-none">
                          {participantMeta?.name || assistant.name}
                        </span>
                      )}
                    </div>
                  ) : (isLast || isActiveAssistantTurn) &&
                    (crewSpeakerShape || !crewMateIdentity) ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <MessageLosiMark
                        size={ICON_SIZE}
                        active={shouldShowAssistantGenerationState}
                        pose={messageLosiPose}
                        shape={crewSpeakerShape}
                      />
                      {crewSpeakerLabel ? (
                        <span
                          className="max-w-[4.5rem] truncate text-[10px] font-medium leading-none"
                          style={
                            crewAccentColor
                              ? { color: crewAccentColor }
                              : undefined
                          }
                        >
                          {crewSpeakerLabel}
                        </span>
                      ) : null}
                    </div>
                  ) : crewSpeakerShape ? (
                    <div className="flex flex-col items-center gap-0.5">
                      <MessageLosiMark
                        size={ICON_SIZE}
                        active={false}
                        pose={messageLosiPose}
                        shape={crewSpeakerShape}
                      />
                      {crewSpeakerLabel ? (
                        <span
                          className="max-w-[4.5rem] truncate text-[10px] font-medium leading-none"
                          style={
                            crewAccentColor
                              ? { color: crewAccentColor }
                              : undefined
                          }
                        >
                          {crewSpeakerLabel}
                        </span>
                      ) : null}
                    </div>
                  ) : crewMateIdentity ? (
                    // Animal / non-shape mate (Fox, Owl, …): no LosiAvatar
                    // engine shape, and the web app does not ship the plush
                    // JPGs (those live in the separate crew-mate app). Render an
                    // accent-colored monogram badge so the mate still reads as a
                    // distinct entity instead of the generic Losi logo.
                    <div className="flex flex-col items-center gap-0.5">
                      <span
                        className="flex size-[32px] items-center justify-center rounded-sm text-xs font-semibold uppercase text-white"
                        style={{
                          backgroundColor: crewAccentColor ?? '#6B7280'
                        }}
                        aria-label={crewMateIdentity.name}
                      >
                        {crewMateIdentity.name.charAt(0)}
                      </span>
                      <span
                        className="max-w-[4.5rem] truncate text-[10px] font-medium leading-none"
                        style={
                          crewAccentColor ? { color: crewAccentColor } : undefined
                        }
                      >
                        {crewSpeakerLabel ?? crewMateIdentity.name}
                      </span>
                    </div>
                  ) : (
                    <Image
                      className="size-[32px] rounded-sm object-contain"
                      src={logo}
                      height={ICON_SIZE}
                      width={ICON_SIZE}
                      alt=""
                    />
                  )}
                </div>
              ) : null}

              {isOtherUserMessage ? (
                <div className="flex shrink-0 items-center">
                  {publicSafeSenderImageUrl ? (
                    <Image
                      className="animate-blur size-[32px] rounded-full"
                      src={publicSafeSenderImageUrl}
                      height={ICON_SIZE}
                      width={ICON_SIZE}
                      alt=""
                    />
                  ) : (
                    <IconMoodSmile
                      className="border-primary bg-primary text-secondary rounded border p-1"
                      size={ICON_SIZE}
                      aria-label={userSenderName || 'User'}
                    />
                  )}
                </div>
              ) : null}

              {showActions ? (
                <div
                  className={cn(
                    // flex-1 (not ml-auto shrink): MessageActions renders
                    // the inline feedback prompt on its own row below the
                    // action row, and that row must span the message width.
                    // The action row itself stays right-aligned inside it.
                    'flex min-w-0 w-full flex-1 items-center gap-2',
                    // The most recent message keeps its actions visible; others
                    // reveal on hover/focus.
                    isLast
                      ? 'opacity-100'
                      : 'opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-state=open]]:opacity-100'
                  )}
                >
                  <MessageActions
                    content={messageText}
                    codeBlocks={codeBlocks}
                    isGenerating={isGenerating}
                    onCopy={handleCopy}
                    onEdit={canEditMessage ? handleStartEdit : undefined}
                    isAssistant={message.role === 'assistant'}
                    isLast={isLast}
                    isEditing={isEditing}
                    onRegenerate={handleRegenerate}
                    isVoiceToTextPlaying={isVoiceToTextPlaying}
                    onVoiceToText={handleVoiceToText}
                    isLoading={isLoading}
                    messageId={message.id}
                    thumbsUp={thumbsUp}
                    sessionId={message.session_id ?? ''}
                    modelId={message.model_id ?? ''}
                    providerId={message.provider_id ?? ''}
                    onFeedbackUpdate={handleFeedbackUpdate}
                    starred={isStarred}
                    onStarUpdate={handleStarUpdate}
                    isSelectionMode={isSelectionMode}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    onPreview={handlePreview}
                    hasNote={hasNote}
                    onSpaceNoteCreated={handleSpaceNoteCreated}
                    onSpaceTaskCreated={handleSpaceTaskCreated}
                    isPublic={isPublic}
                    sharing={message.sharing}
                    position="footer"
                    leadingActions={
                      reactionTargetType && !isPublic && !isEmptyLoadingAssistantTurn ? (
                        <ReactionTarget
                          className="mt-0 shrink-0"
                          disabled={shouldShowAssistantGenerationState}
                          targetId={message.id}
                          targetType={reactionTargetType}
                        />
                      ) : null
                    }
                    showInlineFeedbackPrompt={message.role === 'assistant' && isLast && !isPublic}
                    inlineFeedbackTriggerText={previousUserMessageContent}
                    previousUserMessageContent={previousUserMessageContent}
                    onDelete={handleDeleteMessage}
                    onDuplicateChat={handleDuplicateChat}
                    chatId={message.chat_id}
                  />
                </div>
              ) : null}

              {isOwnMessage ? (
                <div
                  className={cn(
                    'flex shrink-0 items-center',
                    // With actions hidden (public share), nothing else pushes the
                    // avatar to the end — pin it with ms-auto for own/public turns.
                    !showActions ? 'ms-auto' : 'ms-2'
                  )}
                >
                  {publicSafeSenderImageUrl ? (
                    <Image
                      className="animate-blur size-[32px] rounded-full"
                      src={publicSafeSenderImageUrl}
                      height={ICON_SIZE}
                      width={ICON_SIZE}
                      alt=""
                    />
                  ) : publicSafeProfileImageUrl ? (
                    <Image
                      className="animate-blur size-[32px] rounded-full"
                      src={publicSafeProfileImageUrl}
                      height={ICON_SIZE}
                      width={ICON_SIZE}
                      alt=""
                    />
                  ) : (
                    <IconMoodSmile
                      className="border-primary bg-primary text-secondary rounded border p-1"
                      size={ICON_SIZE}
                      aria-label={userSenderName || 'User'}
                    />
                  )}
                </div>
              ) : null}
            </div>

            {/* Attachments: your uploads align with your bubble (right);
                Losi / teammate turns stay left with the assistant column. */}
            {visibleImages.length > 0 ? (
              <div
                className={cn(
                  'mt-1 flex w-full flex-wrap items-start gap-2',
                  isOwnMessage && !isSidekick && 'justify-end',
                  !isSidekick &&
                    (isOtherUserMessage || message.role === 'assistant') &&
                    'justify-start'
                )}
              >
                {visibleImages.map((path, index) => {
                  return (
                    <ResilientImage
                      key={index}
                      className="hover:bg-accent max-h-[min(60vh,520px)] max-w-full cursor-pointer rounded-lg object-contain transition-[background-color,opacity,transform] duration-200 ease-out"
                      src={path}
                      alt="message image"
                      preserveAspectRatio
                      style={{
                        height: 'auto',
                        width: 'auto'
                      }}
                      onClick={() => {
                        setSelectedImage({
                          messageId: message.id,
                          path,
                          base64: '',
                          url: path,
                          file: null
                        })

                        setShowImagePreview(true)
                      }}
                      loading="lazy"
                    />
                  )
                })}
              </div>
            ) : null}
          </motion.div>

          {fileItems.length > 0 && (
            <div className="border-primary mt-6 border-t pt-4 font-semibold">
              {!viewSources ? (
                <div
                  className="hover:bg-accent flex cursor-pointer items-center text-lg"
                  onClick={() => setViewSources(true)}
                >
                  {fileItems.length}
                  {fileItems.length > 1 ? ' Sources ' : ' Source '}
                  from {Object.keys(fileSummary).length}{' '}
                  {Object.keys(fileSummary).length > 1 ? 'Files' : 'File'}{' '}
                  <IconCaretRightFilled className="ml-1" />
                </div>
              ) : (
                <>
                  <div
                    className="hover:bg-accent flex cursor-pointer items-center text-lg"
                    onClick={() => setViewSources(false)}
                  >
                    {fileItems.length}
                    {fileItems.length > 1 ? ' Sources ' : ' Source '}
                    from {Object.keys(fileSummary).length}{' '}
                    {Object.keys(fileSummary).length > 1 ? 'Files' : 'File'}{' '}
                    <IconCaretDownFilled className="ml-1" />
                  </div>

                  <div className="mt-3 space-y-4">
                    {Object.values(fileSummary).map((file, index) => (
                      <div key={index}>
                        <div className="flex items-center space-x-2">
                          <div>
                            <FileIcon type={file.type} />
                          </div>

                          <div className="truncate">{file.name}</div>
                        </div>

                        {fileItems
                          .filter((fileItem) => {
                            const parentFile = files.find(
                              (parentFile) => parentFile.id === fileItem.file_id
                            )
                            return parentFile?.id === file.id
                          })
                          .map((fileItem, index) => (
                            <div
                              key={index}
                              className="hover:bg-accent ml-8 mt-1.5 flex cursor-pointer items-center space-x-2"
                              onClick={() => {
                                setSelectedFileItem(fileItem)
                                setShowFileItemPreview(true)
                              }}
                            >
                              <div className="text-sm font-normal">
                                <span className="mr-1 text-lg font-semibold">-</span>{' '}
                                {getReadableFileItemContent(fileItem.content).substring(0, 200)}
                                ...
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {isEditing && (
            <div className="mt-4 flex justify-center space-x-2">
              <Button size="sm" onClick={handleSendEdit}>
                Save & Send
              </Button>

              <Button size="sm" variant="outline" onClick={onCancelEdit}>
                Cancel
              </Button>
            </div>
          )}
        </div>

        {showImagePreview && selectedImage && (
          <ImageModal
            isOpen={showImagePreview}
            onClose={() => {
              setShowImagePreview(false)
              setSelectedImage(null)
            }}
            src={selectedImage.url || selectedImage.base64 || selectedImage.path}
            alt="message image"
          />
        )}

        {showFileItemPreview && selectedFileItem && (
          <FilePreview
            type="file_item"
            item={selectedFileItem}
            isOpen={showFileItemPreview}
            onOpenChange={(isOpen: boolean) => {
              setShowFileItemPreview(isOpen)
              setSelectedFileItem(null)
            }}
          />
        )}

        {/* Render citations if provided */}
        {allCitationSources.length > 0 && (
          <CitationSourcesList
            sources={allCitationSources}
            idPrefix={`citation-${message.id}`}
            className="mt-4"
            cardClassName="transition-transform hover:-translate-y-0.5"
          />
        )}
      </motion.div>
    )
  }
)

Message.displayName = 'Message'
